<script lang="ts">
    // @ts-nocheck
    import { onMount, untrack } from 'svelte';
    import { fly } from 'svelte/transition';
    import { getDeviceFingerprint } from './lib/fingerprint';
    import { API_BASE, fetchArrivals, fetchCountries, fetchStates, fetchAgenciesByLocation, fetchAgencyLogo, fetchRoutes, fetchStops, fetchTrips, registerDevice, fetchWeather } from './lib/api';
    import { buildDisplayArrivals } from './lib/arrivals';
    import type { TransitData, Agency, Route, Stop, Trip, Arrival } from './lib/types';
    import { Wifi, AlertTriangle, X, Loader2, ChevronDown, Sun, Moon, Cloud, CloudRain, CloudSnow, Wind, RefreshCw } from 'lucide-svelte';
    import { ARRIVALS, UI, POLLING, ALLOWED_COUNTRIES } from './lib/constants';

    // Global error handler to catch Svelte reactivity errors
    if (typeof window !== 'undefined') {
        const globalWithFlags = window as Window & {
            __transitHubGlobalErrorHandlerAttached?: boolean;
        };
        if (!globalWithFlags.__transitHubGlobalErrorHandlerAttached) {
            globalWithFlags.__transitHubGlobalErrorHandlerAttached = true;
            window.addEventListener('error', (event) => {
                console.error('[GLOBAL ERROR HANDLER]', event.error || event.message, event);
            });
            window.addEventListener('unhandledrejection', (event) => {
                console.error('[UNHANDLED REJECTION]', event.reason);
            });
        }
    }

    const CONFIG_STORAGE_KEY = 'kiosk_config_v1';
    const THEME_STORAGE_KEY = 'kiosk_theme_v1';
    const TIME_FORMAT_KEY = 'kiosk_time_format_v1';
    const TEMP_UNIT_KEY = 'kiosk_temp_unit_v1';
    const LOCALE_KEY = 'kiosk_locale_v1';
    const RECENT_ROUTES_KEY = 'kiosk_recent_routes_v1';
    const RECENT_STOPS_KEY = 'kiosk_recent_stops_v1';

    let transitData = $state<TransitData | null>(null);
    let status = $state<'connecting' | 'online' | 'offline'>('connecting');
    let liveFeedMode = $state<'connecting' | 'live' | 'scheduled' | 'offline'>('connecting');
    let currentTime = $state(new Date());
    let lastError = $state<string | null>(null); // P1-6: Error state tracking
    let lastSuccessfulFetch = $state<Date | null>(null); // Track when we last got fresh data from API
    let isSettingsOpen = $state(false);
    let currentArrivalsController: AbortController | null = null; // Request cancellation
    let currentExtendedController: AbortController | null = null; // Extended arrivals cancellation
    let countrySelectEl = $state<HTMLSelectElement | null>(null);
    let stateSelectEl = $state<HTMLSelectElement | null>(null);
    let agencySelectEl = $state<HTMLSelectElement | null>(null);
    let routeSelectEl = $state<HTMLSelectElement | null>(null);
    let stopSelectEl = $state<HTMLSelectElement | null>(null);
    let lastFocusedElement: HTMLElement | null = null;

    let agencies = $state<Agency[]>([]);
    let routes = $state<Route[]>([]);
    let stops = $state<Stop[]>([]);
    let directions = $state<{ id: string; label: string; isDirId?: boolean }[]>([]);

    let countries = $state<string[]>([]);
    let states = $state<string[]>([]);

    let loadingAgencies = $state(false);
    let loadingCountries = $state(false);
    let loadingStates = $state(false);
    let loadingRoutes = $state(false);
    let loadingStops = $state(false);
    let loadingDirections = $state(false);
    let routesError = $state<string | null>(null);
    let stopsError = $state<string | null>(null);
    let directionsError = $state<string | null>(null);
    let routesReloadTick = $state(0);
    let directionsReloadTick = $state(0);
    let agenciesError = $state<string | null>(null);
    let agencyRequestId = 0;
    let statesRequestId = 0;
    let routesRequestId = 0;
    let directionsRequestId = 0;
    let isRestoringConfig = false;
    let usingFallbackCountries = $state(false);
    let configError = $state('');
    let activationToast = $state('');
    let use24Hour = $state(false);
    let useCelsius = $state(true); // Default to Celsius (metric)
    let locale = $state('en-US');
    let weatherData = $state<any>(null);
    let recentRoutes = $state<Route[]>([]);
    let recentStops = $state<Stop[]>([]);
    let showAlerts = $state(false);
    let userHasInteractedWithDirections = $state(false); // Track manual direction changes
    let isRefreshingSelection = $state(false);
    // Using ALLOWED_COUNTRIES from constants.ts
    const FALLBACK_COUNTRIES = [...ALLOWED_COUNTRIES]; // Fallback if API fails

    // Separate state variables for fine-grained reactivity in Svelte 5
    // This prevents infinite loops that happen when a single large config object is updated
    let country = $state('');
    let state = $state('');
    let agency = $state('');
    let agencyName = $state('');
    let route = $state('');
    let routeName = $state('');
    let selectedDirections = $state<string[]>([]);
    let selectedDirectionNames = $state<string[]>([]);
    let stop = $state('');
    let stopName = $state('');

    // Derived config object for backward compatibility with templates and logic
    const config = $derived({
        country,
        state,
        agency,
        agencyName,
        route,
        routeName,
        directions: selectedDirections,
        directionNames: selectedDirectionNames,
        stop,
        stopName
    });

    let currentAlertIndex = $state(0);
    const alerts = $derived(
        transitData?.alerts?.length
            ? transitData.alerts
            : ['No service alerts at this time.']
    );
    const routeDisplayName = $derived.by(() => {
        if (primaryArrival?.routeShortName || primaryArrival?.routeLongName || primaryArrival?.vehicle) {
            return primaryArrival.routeShortName || primaryArrival.routeLongName || primaryArrival.vehicle;
        }
        const liveRoute = transitData?.route;
        if (liveRoute && liveRoute !== 'Offline' && liveRoute !== 'Service Active') {
            return liveRoute;
        }
        return routeName || 'Select a route';
    });
    const stopDisplayName = $derived(stopName || 'Choose a stop');
    const directionDisplayName = $derived(
        selectedDirectionNames.length > 0
            ? selectedDirectionNames.join(', ')
            : 'Select direction(s)'
    );
    const primaryDirectionLabel = $derived.by(() => {
        if (primaryArrival?.headsign) return primaryArrival.headsign;
        if (selectedDirectionNames.length === 1) return selectedDirectionNames[0];
        if (selectedDirectionNames.length > 1) return 'Multiple destinations';
        return 'Select destination(s)';
    });
    // FIXED: Reduced from 360 to 90 minutes to avoid showing arrivals 5+ hours in future
    // Most users care about arrivals in the next 1.5 hours, not scheduled departures far in future
    // Using constant from lib/constants.ts for maintainability
    const normalizeLabel = (value: string) => value.toLowerCase().trim();
    const canonicalHeadsign = (value: string) => {
        const normalized = normalizeLabel(value).replace(/\s+/g, ' ');
        // Strip common route-prefix forms (e.g. "12 - Burlington", "12 Burlington", "A12: Downtown")
        return normalized
            .replace(/^[a-z]?\d+[a-z]?\s*[-:]\s*/i, '')
            .replace(/^[a-z]?\d+[a-z]?\s+/i, '')
            .trim();
    };
    const matchesSelectedDirection = (arrival: Arrival, selectedDir: string) => {
        if (selectedDir === '0' || selectedDir === '1') {
            return arrival.directionId?.toString() === selectedDir;
        }
        if (!arrival.headsign) return false;
        const selectedNormalized = normalizeLabel(selectedDir);
        const headsignNormalized = normalizeLabel(arrival.headsign);
        if (headsignNormalized === selectedNormalized) return true;

        const selectedCanonical = canonicalHeadsign(selectedDir);
        const headsignCanonical = canonicalHeadsign(arrival.headsign);
        if (!selectedCanonical || !headsignCanonical) return false;
        return (
            headsignCanonical === selectedCanonical ||
            headsignCanonical.includes(selectedCanonical) ||
            selectedCanonical.includes(headsignCanonical)
        );
    };
    const selectedRoute = $derived.by(
        () => routes.find((r) => r.onestop_id === route) || null
    );
    const selectedStop = $derived.by(
        () => stops.find((s) => s.onestop_id === stop) || null
    );
    const stopCoordinates = $derived.by(() => {
        if (!selectedStop?.geometry?.coordinates) return null;
        const [lon, lat] = selectedStop.geometry.coordinates;
        return { lat, lon };
    });
    const routeAccent = $derived.by(() => {
        const color = selectedRoute?.route_color;
        if (!color) return null;
        const normalized = color.replace('#', '').trim();
        if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
        return `#${normalized}`;
    });

    const applyArrivalFilters = (arrivals: Arrival[], maxMinutes = 90) => {
        const currentDirs = selectedDirections;
        // Smart grace period for arrival removal:
        // - Real-time arrivals: remove after -1 min (GPS tracking is accurate, vehicle has departed)
        // - Scheduled arrivals: remove after -2 min (account for boarding time and schedule padding)
        const withinWindow = arrivals.filter((arrival) => {
            const minThreshold = arrival.isRealtime ? -1 : -2;
            return arrival.minutes >= minThreshold && arrival.minutes <= maxMinutes;
        });
        const routeFiltered = route
            ? withinWindow.filter((arrival) => {
                // If arrival has route info, verify it matches
                if (arrival.routeOnestopId) {
                    return arrival.routeOnestopId === route;
                }
                if (selectedRoute?.route_short_name && arrival.routeShortName) {
                    return arrival.routeShortName === selectedRoute.route_short_name;
                }
                if (selectedRoute?.route_long_name && arrival.routeLongName) {
                    return arrival.routeLongName === selectedRoute.route_long_name;
                }
                // If no route info in arrival data, assume it's correct (we fetched it for this route/stop)
                return true;
            })
            : withinWindow;
        if (!currentDirs.length) {
            // ENHANCED: Prioritize live (real-time) arrivals over scheduled
            // Sort by: 1) Real-time first, 2) Then by time
            return [...routeFiltered].sort((a, b) => {
                if (a.isRealtime !== b.isRealtime) {
                    return a.isRealtime ? -1 : 1; // Real-time first
                }
                return a.minutes - b.minutes;
            });
        }
        const filtered = routeFiltered.filter((arrival) => {
            return currentDirs.some((selectedDir) => matchesSelectedDirection(arrival, selectedDir));
        });

        // ENHANCED: Prioritize live arrivals, then sort by time
        return filtered.sort((a, b) => {
            if (a.isRealtime !== b.isRealtime) {
                return a.isRealtime ? -1 : 1; // Real-time first
            }
            return a.minutes - b.minutes;
        });
    };

    const filteredArrivals = $derived.by(() => applyArrivalFilters(transitData?.arrivals || []));
    const hasLongWaits = $derived.by(() => {
        const arrivals = transitData?.arrivals || [];
        if (!arrivals.length) return false;
        return arrivals.every((arrival) => arrival.minutes > ARRIVALS.MAX_MINUTES);
    });
    const primaryArrival = $derived.by(() => dashboardArrivals[0] || null);
    let extendedArrivals = $state<Arrival[]>([]);
    const extendedFilteredArrivals = $derived.by(() => {
        if (!extendedArrivals.length) return [];
        // Use extended window to include next service day when required.
        return applyArrivalFilters(extendedArrivals, ARRIVALS.EXTENDED_WINDOW);
    });
    const dashboardArrivals = $derived.by(() => buildDisplayArrivals(filteredArrivals, extendedFilteredArrivals, 4));
    const sidebarArrivals = $derived.by(() => dashboardArrivals.slice(1, 4));

    // Sidebar arrivals are capped at 3 maximum
    // Don't create null slots - just render what we have
    const mainEta = $derived.by(() => formatDuration(primaryArrival?.minutes));
    const lastUpdated = $derived(transitData?.timestamp ? new Date(transitData.timestamp) : null);
    // NOTE: Using plain variables, not $state, to avoid triggering reactivity loops
    let lastExtendedKey = '';
    let lastExtendedWindow = 0;

    // Helper text for disabled stops dropdown
    const stopsDisabledReason = $derived.by(() => {
        if (!route) return 'Select a route first';
        if (loadingDirections) return 'Loading destinations...';
        if (directions.length > 0 && selectedDirections.length === 0) {
            return 'Select direction(s) to see stops';
        }
        return null;
    });

    // Data freshness indicator (how old is the data)
    const dataFreshness = $derived.by(() => {
        // Use lastSuccessfulFetch to track time since we got fresh data from API
        // This correctly shows staleness when offline, not just when page loaded
        if (!lastSuccessfulFetch) return 'unknown';
        const ageMs = Date.now() - lastSuccessfulFetch.getTime();
        const ageMinutes = Math.floor(ageMs / 60000);
        if (ageMinutes < 1) return 'fresh'; // < 1 min
        if (ageMinutes < 3) return 'recent'; // 1-3 min
        if (ageMinutes < 10) return 'stale'; // 3-10 min
        return 'very_stale'; // 10+ min
    });

    // Data confidence (real-time vs scheduled)
    const dataConfidence = $derived.by(() => {
        const arrivals = filteredArrivals;
        if (!arrivals.length) return 'none';
        const realtimeCount = arrivals.filter(a => a.isRealtime).length;
        const percentage = (realtimeCount / arrivals.length) * 100;
        if (percentage >= 75) return 'high'; // Most are real-time
        if (percentage >= 25) return 'medium'; // Mixed
        return 'low'; // Mostly scheduled
    });

    let theme = $state<'light' | 'dark'>('dark');
    let hasHydratedConfig = $state(false);

    const formatAgencyName = (agency: Agency) =>
        agency.name || agency.short_name || agency.agency_name || agency.operator?.name || 'Unknown agency';

    const formatRouteName = (route: Route) =>
        [route.route_short_name, route.route_long_name].filter(Boolean).join(' • ') || 'Route';

    const formatStopName = (stop: Stop) => {
        const baseName = stop.stop_name || 'Stop';
        if (stop.platform_code) {
            return `${baseName} - Platform ${stop.platform_code}`;
        }
        return baseName;
    };

    const normalizeList = (values: (string | null | undefined)[]) =>
        [...new Set(values.filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b));

    const formatLoadFactor = (value?: string | null) => {
        if (!value) return null;
        const normalized = value.toUpperCase();
        switch (normalized) {
            case 'EMPTY':
            case 'MANY_SEATS_AVAILABLE':
                return 'Low';
            case 'FEW_SEATS_AVAILABLE':
                return 'Medium';
            case 'STANDING_ROOM_ONLY':
            case 'CRUSHED_STANDING_ROOM_ONLY':
                return 'High';
            case 'FULL':
            case 'NOT_ACCEPTING_PASSENGERS':
                return 'Full';
            default:
                return null;
        }
    };

    const formatDuration = (minutes?: number | null) => {
        if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
            return { value: '--', unit: 'min', isHours: false };
        }
        const rounded = Math.max(0, Math.round(minutes));
        if (rounded >= 60) {
            const hours = Math.floor(rounded / 60);
            const mins = rounded % 60;
            return { value: `${hours}h${mins ? ` ${mins}m` : ''}`, unit: '', isHours: true };
        }
        return { value: `${rounded}`, unit: 'min', isHours: false };
    };

    const formatArrivalTime = (minutes?: number | null) => {
        if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
            return '--';
        }
        const arrivalTime = new Date(Date.now() + minutes * 60000);
        return arrivalTime.toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: !use24Hour,
        });
    };

    const ROUTE_TYPE_LABELS: Record<number, string> = {
        0: 'Tram',
        1: 'Subway',
        2: 'Rail',
        3: 'Bus',
        4: 'Ferry',
        5: 'Cable Car',
        6: 'Gondola',
        7: 'Funicular',
        11: 'Trolleybus',
        12: 'Monorail',
    };

    const getRouteTypeLabel = (route: Route) => {
        const type = route.route_type;
        if (type === undefined || type === null) return 'Other';
        return ROUTE_TYPE_LABELS[type] || 'Other';
    };

    const routeSort = (a: Route, b: Route) => {
        const aKey = a.route_short_name || a.route_long_name || '';
        const bKey = b.route_short_name || b.route_long_name || '';
        const aNum = Number(aKey);
        const bNum = Number(bKey);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
        }
        return aKey.localeCompare(bKey);
    };

    const dedupeRoutes = (items: Route[]) => {
        const score = (route: Route) => {
            let value = 0;
            if (route.onestop_id) value += 3;
            if (route.route_short_name) value += 2;
            if (route.route_long_name) value += 2;
            if (route.route_color) value += 1;
            if (route.route_text_color) value += 1;
            if (route.route_type !== undefined && route.route_type !== null) value += 1;
            return value;
        };
        const map = new Map<string, Route>();
        items.forEach((route) => {
            const key = route.onestop_id
                ? `id:${route.onestop_id}`
                : [
                    'meta',
                    route.route_short_name?.trim() || '',
                    route.route_long_name?.trim() || '',
                    route.route_type ?? '',
                ].join('|');
            if (!map.has(key)) {
                map.set(key, route);
                return;
            }
            const existing = map.get(key);
            if (existing && score(route) > score(existing)) {
                map.set(key, route);
            }
        });
        return Array.from(map.values());
    };

    const dedupeStops = (items: Stop[]) => {
        const seen = new Set<string>();
        return items.filter((stop) => {
            if (!stop?.onestop_id) return false;
            if (seen.has(stop.onestop_id)) return false;
            seen.add(stop.onestop_id);
            return true;
        });
    };

    // CRITICAL: Helper to set directions with equality check to prevent infinite loops
    // Svelte 5 triggers effects when array references change, even if content is identical
    let pendingDirectionAutoSelectKey = '';
    function scheduleDirectionAutoSelect(directionIds: string[]) {
        const autoSelectKey = directionIds.join('|');
        if (!autoSelectKey || pendingDirectionAutoSelectKey === autoSelectKey) return;
        pendingDirectionAutoSelectKey = autoSelectKey;
        // Use queueMicrotask to ensure directions state is committed before auto-select
        queueMicrotask(() => {
            if (pendingDirectionAutoSelectKey !== autoSelectKey) return;
            pendingDirectionAutoSelectKey = '';
            autoSelectDirectionIfNeeded();
        });
    }

    function setDirectionsIfChanged(newDirs: typeof directions, skipAutoSelect = false) {
        const currentIds = directions.map(d => d.id).join('|');
        const nextIds = newDirs.map(d => d.id).join('|');
        if (currentIds !== nextIds) {
            directions = newDirs;
            // EXPLICIT: Trigger auto-select after directions change (unless skipped)
            if (!skipAutoSelect && newDirs.length > 0 && !loadingDirections) {
                scheduleDirectionAutoSelect(newDirs.map((d) => d.id));
            }
        }
    }

    // CRITICAL: Helper to set stops with equality check to prevent infinite loops
    function setStopsIfChanged(newStops: Stop[]) {
        const currentIds = stops.map(s => s.onestop_id).join('|');
        const nextIds = newStops.map(s => s.onestop_id).join('|');
        if (currentIds !== nextIds) {
            stops = newStops;
        }
    }

    // Prevent duplicate stop loads for the same selection while a request is in flight.
    let activeStopsLoadKey = '';
    let stopsLoadRequestId = 0;
    function invalidateStopsLoading() {
        stopsLoadRequestId += 1;
        activeStopsLoadKey = '';
        loadingStops = false;
    }

    // EXPLICIT auto-select direction - called after directions are loaded
    // This replaces the auto-select direction effect to prevent loops
    function autoSelectDirectionIfNeeded() {
        if (directions.length === 0) return;
        if (loadingDirections) return;
        if (userHasInteractedWithDirections) return;

        // Only auto-select if there's exactly one direction
        if (directions.length === 1) {
            const only = directions[0];
            if (selectedDirections.length !== 1 || selectedDirections[0] !== only.id) {
                selectedDirections = [only.id];
                selectedDirectionNames = [only.label];
                try {
                    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({
                        country, state, agency, agencyName, route, routeName,
                        directions: selectedDirections, directionNames: selectedDirectionNames,
                        stop, stopName
                    }));
                } catch (e) {}
                // After auto-selecting direction, load stops
                loadStopsForCurrentSelection();
            }
            return;
        }

        // Validate that selected directions still exist in available directions
        const validDirectionIds = new Set(directions.map((d) => d.id));
        const nextDirections = selectedDirections.filter((dir) => validDirectionIds.has(dir));
        if (nextDirections.length !== selectedDirections.length) {
            const nextNames = directions
                .filter((dir) => nextDirections.includes(dir.id))
                .map((dir) => dir.label);
            selectedDirections = nextDirections;
            selectedDirectionNames = nextNames;
            try {
                localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({
                    country, state, agency, agencyName, route, routeName,
                    directions: selectedDirections, directionNames: selectedDirectionNames,
                    stop, stopName
                }));
            } catch (e) {}
        }
    }

    // EXPLICIT stops loading - called after direction selection
    function loadStopsForCurrentSelection() {
        const currentRoute = route;
        const selectedDirs = selectedDirections;
        const availableDirs = directions;
        stopsError = null;

        if (!currentRoute) {
            setStopsIfChanged([]);
            invalidateStopsLoading();
            return;
        }

        if (availableDirs.length > 0 && selectedDirs.length === 0) {
            setStopsIfChanged([]);
            invalidateStopsLoading();
            return;
        }

        // Fetch stops for selected destination(s). For multi-select we pass all selected
        // directions so backend returns only relevant platforms/stops.
        const directionParams = selectedDirs.length > 0
            ? [...new Set(selectedDirs.map((dir) => dir.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
            : undefined;
        const directionToken = directionParams?.join('||') || 'all';
        const requestKey = `${currentRoute}::${directionToken}`;
        if (activeStopsLoadKey === requestKey) {
            console.log('[STOPS EXPLICIT] Skipping duplicate in-flight load for key:', requestKey);
            return;
        }
        const requestId = ++stopsLoadRequestId;
        activeStopsLoadKey = requestKey;

        console.log('[STOPS EXPLICIT] Loading stops for route:', currentRoute, 'directions:', selectedDirs);
        loadingStops = true;

        const cacheKey = `stops_cache_v3_${currentRoute}_${directionToken}`;

        // Try cache first
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setStopsIfChanged(parsed);
                }
            }
        } catch (e) {}

        // Fetch fresh data
        fetchStops(currentRoute, directionParams)
            .then((data) => {
                if (requestId !== stopsLoadRequestId) {
                    console.log('[STOPS EXPLICIT] Ignoring stale stops response for key:', requestKey);
                    return;
                }
                const nextStops = dedupeStops(data.stops || []);
                setStopsIfChanged(nextStops);
                try {
                    localStorage.setItem(cacheKey, JSON.stringify(nextStops));
                } catch (e) {}
            })
            .catch((err) => {
                if (requestId !== stopsLoadRequestId) return;
                console.error('Failed to load stops for current selection:', err);
                stopsError = formatErrorMessage(err);
            })
            .finally(() => {
                if (requestId !== stopsLoadRequestId) return;
                if (activeStopsLoadKey === requestKey) {
                    activeStopsLoadKey = '';
                }
                loadingStops = false;
                // After stops load, auto-select if only one stop
                autoSelectStopIfNeeded();
            });
    }

    // EXPLICIT auto-select stop - called after stops are loaded
    function autoSelectStopIfNeeded() {
        if (stops.length !== 1) return;
        if (loadingStops) return;

        const only = stops[0];
        if (stop === only.onestop_id) return;

        updateConfig({
            stop: only.onestop_id,
            stopName: formatStopName(only),
        });
    }

    function getSortedAgencies() {
        if (!agencies || !Array.isArray(agencies)) return [];
        try {
            return [...agencies].sort((a, b) => {
                const nameA = formatAgencyName(a);
                const nameB = formatAgencyName(b);
                return nameA.localeCompare(nameB);
            });
        } catch (e) {
            console.error('getSortedAgencies error:', e);
            return agencies;
        }
    }

    const agencyMatchCount = $derived(agencies?.length || 0);
    const selectedAgency = $derived(
        agencies?.find((a) => a.onestop_id === agency) || null
    );
    let logoUrl = $state('');
    let logoRequestId = 0;

    const groupedRoutes = $derived.by(() => {
        const groups = new Map<string, Route[]>();
        const sortedRoutes = [...routes].sort(routeSort);
        sortedRoutes.forEach((route) => {
            const label = getRouteTypeLabel(route);
            if (!groups.has(label)) groups.set(label, []);
            groups.get(label)?.push(route);
        });
        return Array.from(groups.entries()).map(([label, routes]) => ({
            label,
            routes: routes.sort(routeSort),
        }));
    });

    const recentRouteOptions = $derived.by(() =>
        recentRoutes.filter((route) => routes.some((item) => item.onestop_id === route.onestop_id))
    );

    const recentStopOptions = $derived.by(() =>
        recentStops.filter((stop) => stops.some((item) => item.onestop_id === stop.onestop_id))
    );

    const countryExistsInOptions = $derived.by(() =>
        !!country && countries.includes(country)
    );
    const stateExistsInOptions = $derived.by(() =>
        !!state && states.includes(state)
    );
    const agencyExistsInOptions = $derived.by(() =>
        !!agency && agencies.some((item) => item.onestop_id === agency)
    );
    const routeExistsInOptions = $derived.by(() =>
        !!route && routes.some((item) => item.onestop_id === route)
    );
    const stopExistsInOptions = $derived.by(() =>
        !!stop && stops.some((item) => item.onestop_id === stop)
    );

    // Weather helper functions
    function getWeatherIcon(weatherCode: number) {
        // WMO Weather interpretation codes
        if (weatherCode === 0) return Sun; // Clear sky
        if (weatherCode <= 3) return Cloud; // Partly cloudy
        if (weatherCode >= 51 && weatherCode <= 67) return CloudRain; // Rain
        if (weatherCode >= 71 && weatherCode <= 77) return CloudSnow; // Snow
        if (weatherCode >= 95) return CloudRain; // Thunderstorm
        return Cloud; // Default
    }

    function convertTemp(celsius: number): number {
        return useCelsius ? celsius : (celsius * 9/5) + 32;
    }

    function formatTemp(celsius: number): string {
        const temp = Math.round(convertTemp(celsius));
        return `${temp}°${useCelsius ? 'C' : 'F'}`;
    }

    $effect(() => {
        const agency = selectedAgency;
        logoUrl = '';
        const currentRequest = ++logoRequestId;

        if (!agency) return;

        const name = formatAgencyName(agency);
        const WIKIDATA_OVERRIDES: Record<string, string> = {
            'o-dpz2-bramptontransit': 'Q4956262',
        };
        const wikidataOverride = WIKIDATA_OVERRIDES[agency.onestop_id];
        const candidateNames = [
            name,
            agency.short_name,
            agency.name,
            agency.agency_name,
            agency.operator?.name,
            agency.operator?.short_name,
        ].filter(Boolean) as string[];
        if (!candidateNames.length || candidateNames[0] === 'Unknown agency') return;

        (async () => {
            try {
                let wikiLogo: string | null = null;
                for (const candidate of candidateNames) {
                    wikiLogo = await fetchAgencyLogo(
                        candidate,
                        wikidataOverride || agency.tags?.wikidata_id || null
                    );
                    if (wikiLogo) break;
                }
                if (currentRequest !== logoRequestId) return;
                if (wikiLogo) {
                    logoUrl = wikiLogo;
                    return;
                }

                const website = agency.website || agency.agency_url || agency.operator?.website;
                if (!website) return;
                try {
                    const host = new URL(website.startsWith('http') ? website : `https://${website}`).hostname;
                    // CRITICAL FIX: Check request is still current before setting Clearbit logo
                    if (currentRequest !== logoRequestId) return;
                    logoUrl = `https://logo.clearbit.com/${host}?size=160`;
                } catch (err) {
                    if (currentRequest !== logoRequestId) return;
                    logoUrl = '';
                }
            } catch (e) {
                // CRITICAL FIX: Properly handle promise rejection
                console.warn('Failed to fetch agency logo:', e);
                if (currentRequest !== logoRequestId) return;
                logoUrl = '';
            }
        })().catch(err => {
            // CRITICAL FIX: Catch any unhandled promise rejections from async IIFE
            console.error('Unhandled error in logo fetch:', err);
        });
    });

    // CRITICAL: Convert technical errors to user-friendly messages
    function formatErrorMessage(error: unknown): string {
        const message = error instanceof Error ? error.message : String(error);

        // Handle specific error types
        if (message.includes('rate_limit') || message.includes('429')) {
            return 'Too many requests. Please wait a moment and try again.';
        }
        if (message.includes('503') || message.includes('unavailable')) {
            return 'Transit data service is temporarily unavailable. Please try again in a few moments.';
        }
        if (message.includes('404') || message.includes('not found')) {
            return 'Transit data not found for this location. Try a different stop or route.';
        }
        if (message.includes('timeout') || message.includes('aborted')) {
            return 'Request timed out. Please check your internet connection.';
        }
        if (message.includes('parse') || message.includes('JSON')) {
            return 'Received invalid data from transit service. Please try again.';
        }
        if (message.includes('network') || message.includes('fetch')) {
            return 'Network error - unable to connect to transit data service.';
        }

        // Default fallback for unknown errors
        return 'Unable to load transit data. Please try again.';
    }

    function updateConfig(next: Partial<typeof config>) {
        // CRITICAL FIX: Update individual $state variables for fine-grained reactivity
        // This is the most reliable way to prevent infinite loops in Svelte 5
        let changed = false;

        // Use untrack for ALL reads and assignments to prevent the caller effect from tracking everything
        untrack(() => {
            if (next.country !== undefined && country !== next.country) {
                country = next.country;
                changed = true;
            }
            if (next.state !== undefined && state !== next.state) {
                state = next.state;
                changed = true;
            }
            if (next.agency !== undefined && agency !== next.agency) {
                agency = next.agency;
                changed = true;
            }
            if (next.agencyName !== undefined && agencyName !== next.agencyName) {
                agencyName = next.agencyName;
                changed = true;
            }
            if (next.route !== undefined && route !== next.route) {
                route = next.route;
                changed = true;
            }
            if (next.routeName !== undefined && routeName !== next.routeName) {
                routeName = next.routeName;
                changed = true;
            }
            if (next.directions !== undefined) {
                if (JSON.stringify(selectedDirections) !== JSON.stringify(next.directions)) {
                    selectedDirections = [...next.directions];
                    changed = true;
                }
            }
            if (next.directionNames !== undefined) {
                if (JSON.stringify(selectedDirectionNames) !== JSON.stringify(next.directionNames)) {
                    selectedDirectionNames = [...next.directionNames];
                    changed = true;
                }
            }
            if (next.stop !== undefined && stop !== next.stop) {
                stop = next.stop;
                changed = true;
            }
            if (next.stopName !== undefined && stopName !== next.stopName) {
                stopName = next.stopName;
                changed = true;
            }

            if (changed) {
                try {
                    // Save the whole state to localStorage - MUST be inside untrack
                    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({
                        country, state, agency, agencyName, route, routeName,
                        directions: selectedDirections, directionNames: selectedDirectionNames,
                        stop, stopName
                    }));
                } catch (e) {}
            }
        });
    }

    function setTheme(next: 'light' | 'dark') {
        theme = next;
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch (e) {}
    }

    function setTimeFormat(next: boolean) {
        use24Hour = next;
        try {
            localStorage.setItem(TIME_FORMAT_KEY, next ? '24' : '12');
        } catch (e) {}
    }

    function setTempUnit(celsius: boolean) {
        useCelsius = celsius;
        try {
            localStorage.setItem(TEMP_UNIT_KEY, celsius ? 'C' : 'F');
        } catch (e) {}
    }

    function setLocale(next: string) {
        locale = next;
        try {
            localStorage.setItem(LOCALE_KEY, next);
        } catch (e) {}
    }

    const loadRecent = <T,>(key: string): T[] => {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    const storeRecent = (key: string, list: unknown[]) => {
        try {
            localStorage.setItem(key, JSON.stringify(list.slice(0, 6)));
        } catch (e) {}
    };

    const rememberRoute = (route: Route | undefined) => {
        if (!route) return;
        const next = [
            route,
            ...recentRoutes.filter((item) => item.onestop_id !== route.onestop_id),
        ];
        recentRoutes = next.slice(0, 6);
        storeRecent(RECENT_ROUTES_KEY, recentRoutes);
    };

    const rememberStop = (stop: Stop | undefined) => {
        if (!stop) return;
        const next = [
            stop,
            ...recentStops.filter((item) => item.onestop_id !== stop.onestop_id),
        ];
        recentStops = next.slice(0, 6);
        storeRecent(RECENT_STOPS_KEY, recentStops);
    };

    function retryLocationLoad() {
        // FIXED: Use individual state variables instead of config to avoid reactive dependencies
        if (!country) {
            reloadCountries();
            return;
        }
        if (!state) {
            reloadStates(country);
            return;
        }
        reloadAgencies(country, state);
    }

    function retryRoutesLoad() {
        if (!agency) return;
        routesError = null;
        routesReloadTick += 1;
    }

    function retryDirectionsLoad() {
        if (!route) return;
        directionsError = null;
        directionsReloadTick += 1;
    }

    function retryStopsLoad() {
        if (!route) return;
        stopsError = null;
        loadStopsForCurrentSelection();
    }

    async function waitForCondition(
        predicate: () => boolean,
        timeoutMs = 15000,
        pollMs = 50
    ): Promise<boolean> {
        const started = Date.now();
        while (Date.now() - started < timeoutMs) {
            if (predicate()) return true;
            await new Promise((resolve) => setTimeout(resolve, pollMs));
        }
        return predicate();
    }

    async function refreshCurrentSelection() {
        if (isRefreshingSelection) return;
        isRefreshingSelection = true;
        lastError = null;
        routesError = null;
        directionsError = null;
        stopsError = null;
        configError = '';

        try {
            // Rehydrate the currently selected cascade without clearing user selections.
            if (!country) {
                await reloadCountries();
                return;
            }
            if (country) {
                await reloadStates(country);
            }
            if (country && state) {
                await reloadAgencies(country, state);
            } else if (country) {
                await reloadAgencies(country);
                return;
            }
            if (agency) {
                routesReloadTick += 1;
                await waitForCondition(() => !loadingRoutes);
            }
            if (route) {
                directionsReloadTick += 1;
                await waitForCondition(() => !loadingDirections);
                loadStopsForCurrentSelection();
                await waitForCondition(() => !loadingStops);
            }
            if (stop) {
                await loadArrivals(stop);
            }
        } finally {
            isRefreshingSelection = false;
        }
    }

    function handleCountryChange(value: string) {
        try {
            invalidateStopsLoading();
            routesError = null;
            directionsError = null;
            stopsError = null;
            updateConfig({
                country: value,
                state: '',
                agency: '',
                agencyName: '',
                route: '',
                routeName: '',
                directions: [],
                directionNames: [],
                stop: '',
                stopName: '',
            });
            configError = '';
            // EXPLICIT CASCADE: Load states and agencies for new country
            if (value) {
                reloadStates(value);
                agencies = [];
            } else {
                statesRequestId += 1;
                states = [];
                agencies = [];
            }
        } catch (e) {
            console.error('handleCountryChange error:', e);
        }
    }

    function handleStateChange(value: string) {
        try {
            invalidateStopsLoading();
            routesError = null;
            directionsError = null;
            stopsError = null;
            updateConfig({
                state: value,
                agency: '',
                agencyName: '',
                route: '',
                routeName: '',
                directions: [],
                directionNames: [],
                stop: '',
                stopName: '',
            });
            configError = '';
            // EXPLICIT CASCADE: Load agencies for country + state
            const currentCountry = country; // Read from state, not config
            if (currentCountry) {
                if (value) {
                    reloadAgencies(currentCountry, value);
                } else {
                    reloadAgencies(currentCountry);
                }
            }
        } catch (e) {
            console.error('handleStateChange error:', e);
        }
    }

    function handleAgencyChange(value: string) {
        try {
            const selected = agencies.find((agency) => agency.onestop_id === value);
            // Clear directions immediately to prevent UI state issues
            invalidateStopsLoading();
            routesError = null;
            directionsError = null;
            stopsError = null;
            setDirectionsIfChanged([]);
            setStopsIfChanged([]);
            updateConfig({
                agency: value,
                agencyName: selected ? formatAgencyName(selected) : '',
                route: '',
                routeName: '',
                directions: [],
                directionNames: [],
                stop: '',
                stopName: '',
            });
            configError = '';
        } catch (e) {
            console.error('handleAgencyChange error:', e);
        }
    }

    function handleRouteChange(value: string) {
        const selected = routes.find((route) => route.onestop_id === value);
        // Clear directions immediately to prevent UI state issues
        invalidateStopsLoading();
        pendingDirectionAutoSelectKey = '';
        directionsError = null;
        stopsError = null;
        setDirectionsIfChanged([]);
        setStopsIfChanged([]);
        // FIXED: Reset interaction flag so auto-select can work for new route
        userHasInteractedWithDirections = false;
        updateConfig({
            route: value,
            routeName: selected ? formatRouteName(selected) : '',
            directions: [],
            directionNames: [],
            stop: '',
            stopName: '',
        });
        configError = '';
        rememberRoute(selected);
    }

    function handleDirectionToggle(directionId: string, directionLabel: string) {
        // FIXED: Mark that user has manually interacted with directions
        // This prevents auto-select from overriding user's explicit choices
        userHasInteractedWithDirections = true;

        // FIXED: Use individual state variables instead of config to avoid reactive dependencies
        const currentDirections = [...selectedDirections];
        const currentNames = [...selectedDirectionNames];
        const index = currentDirections.indexOf(directionId);

        if (index > -1) {
            // Remove if already selected
            currentDirections.splice(index, 1);
            currentNames.splice(index, 1);
        } else {
            // Add if not selected
            currentDirections.push(directionId);
            currentNames.push(directionLabel);
        }

        updateConfig({
            directions: currentDirections,
            directionNames: currentNames,
        });
        stopsError = null;
        configError = '';

        // EXPLICIT: Load stops after direction change (no effect needed)
        loadStopsForCurrentSelection();
    }

    function handleStopChange(value: string) {
        const selected = stops.find((stop) => stop.onestop_id === value);
        updateConfig({
            stop: value,
            stopName: selected ? formatStopName(selected) : '',
        });
        configError = '';
        rememberStop(selected);
    }

    function activateKiosk() {
        if (!stop) {
            configError = 'Please select a stop before activating the kiosk.';
            return;
        }
        if (directions.length > 0 && (!selectedDirections || selectedDirections.length === 0)) {
            configError = 'Please select at least one direction.';
            return;
        }
        loadArrivals(stop);
        isSettingsOpen = false;
        const directionLabel = selectedDirectionNames.length > 0
            ? ` - ${selectedDirectionNames.join(', ')}`
            : '';
        const lineLabel = routeName || 'Line';
        activationToast = `Kiosk configured for ${lineLabel}${directionLabel} at ${stopName || 'selected stop'}`;
        setTimeout(() => {
            activationToast = '';
        }, 2500);
    }

    // CRITICAL FIX: Request ID tracking to prevent race condition
    let arrivalsRequestId = 0;

    async function loadArrivals(stopId: string) {
        try {
            // FIXED: Increment request ID to track this specific request
            const requestId = ++arrivalsRequestId;

            // FIXED: Cancel any in-flight request before starting a new one
            if (currentArrivalsController) {
                currentArrivalsController.abort();
                console.log('Cancelled previous arrivals request');
            }

            // Create new controller for this request
            currentArrivalsController = new AbortController();
            const signal = currentArrivalsController.signal;

            // FIXED: Don't filter by direction at backend when multiple directions selected
            // Backend filtering would exclude arrivals for other selected directions
            // Client-side filtering handles multiple directions correctly
            const directionParam = selectedDirections.length === 1 ? selectedDirections[0] : undefined;
            const result = await fetchArrivals(stopId, directionParam, undefined, signal);

            // CRITICAL: Check if this request is still current before updating state
            // If user changed stop while request was in-flight, discard stale response
            if (requestId !== arrivalsRequestId) {
                console.log('Discarding stale arrivals response');
                return;
            }

            // Clear controller on success
            currentArrivalsController = null;

            transitData = result.data || {
                route: 'Offline',
                arrivals: [],
                alerts: ['Unable to load arrivals. Please try again shortly.'],
                timestamp: Date.now()
            };
            lastError = null; // P1-6: Clear error on success
            if (result.source === 'api') {
                status = 'online';
                liveFeedMode = 'live';
                lastSuccessfulFetch = new Date(); // Track when we got fresh data
            } else if (result.source === 'cache') {
                status = 'online';
                liveFeedMode = 'scheduled';
                // Don't update lastSuccessfulFetch - using stale cached data
            } else if (result.source === 'no_data') {
                status = 'connecting';
                liveFeedMode = 'scheduled';
                if (result.error) {
                    lastError = typeof result.error === 'string'
                        ? result.error
                        : (result.error.message || null);
                }
            } else {
                status = 'offline';
                liveFeedMode = 'offline';
                if (result.error) {
                    lastError = typeof result.error === 'string'
                        ? result.error
                        : (result.error.message || 'Transit data service is currently unavailable.');
                } else {
                    lastError = 'Transit data service is currently unavailable.';
                }
            }
        } catch (e) {
            // Ignore abort errors (expected when user changes selection quickly)
            if (e instanceof Error && e.name === 'AbortError') {
                console.log('Arrivals request aborted (expected)');
                return;
            }

            currentArrivalsController = null;
            status = 'offline';
            liveFeedMode = 'offline';
            // CRITICAL: Set user-friendly error message
            lastError = formatErrorMessage(e);
            console.error('Failed to load arrivals:', e);
            // Set empty data structure to prevent UI errors
            transitData = {
                route: 'Offline',
                arrivals: [],
                alerts: ['Unable to load arrivals. Please check your connection.'],
                timestamp: Date.now()
            };
        }
    }

    async function reloadCountries() {
        loadingCountries = true;
        agenciesError = null;
        usingFallbackCountries = false;
        try {
            const data = await fetchCountries();
            countries = data.filter((name) => ALLOWED_COUNTRIES.includes(name));
        } catch (e) {
            countries = FALLBACK_COUNTRIES;
            agenciesError = formatErrorMessage(e);
            usingFallbackCountries = true;
        } finally {
            loadingCountries = false;
        }
    }

    async function reloadStates(country: string) {
        const requestId = ++statesRequestId;
        try {
            loadingStates = true;
            agenciesError = null;
            console.log('Fetching states for country:', country);
            const nextStates = await fetchStates(country);
            if (requestId !== statesRequestId) return;
            states = nextStates;
            console.log('Successfully loaded states:', states.length);
        } catch (e) {
            if (requestId !== statesRequestId) return;
            console.error('reloadStates error:', e);
            console.error('Error details:', {
                name: e instanceof Error ? e.name : 'Unknown',
                message: e instanceof Error ? e.message : String(e),
                stack: e instanceof Error ? e.stack : undefined
            });
            states = [];
            agenciesError = formatErrorMessage(e);
        } finally {
            if (requestId === statesRequestId) {
                loadingStates = false;
            }
        }
    }

    async function reloadAgencies(country: string, state?: string) {
        agencyRequestId += 1;
        const requestId = agencyRequestId;

        try {
            loadingAgencies = true;
            agenciesError = null;

            const results = await fetchAgenciesByLocation(country, state);

            if (requestId !== agencyRequestId) return;

            // Deduplicate agencies by onestop_id
            const uniqueAgencies = Array.from(
                new Map((results || []).map(a => [a.onestop_id, a])).values()
            );

            agencies = uniqueAgencies;
        } catch (e) {
            console.error('reloadAgencies error:', e);
            if (requestId === agencyRequestId) {
                agencies = [];
                agenciesError = formatErrorMessage(e);
            }
        } finally {
            if (requestId === agencyRequestId) {
                loadingAgencies = false;
            }
        }
    }

    onMount(() => {
        let isUnmounted = false;
        const timer = setInterval(() => {
            currentTime = new Date();
            if (transitData?.arrivals) {
                // CRITICAL FIX: Recalculate minutes from arrivalTimestamp to prevent drift
                // This eliminates floating-point accumulation errors completely
                transitData.arrivals = transitData.arrivals.map((a) => {
                    // If we have the arrival timestamp, recalculate from scratch (drift-free)
                    if (a.arrivalTimestamp) {
                        const minutesUntilArrival = (a.arrivalTimestamp - Date.now()) / 60000;
                        return {
                            ...a,
                            minutes: minutesUntilArrival,
                        };
                    }
                    // Fallback: decrement if no timestamp (backward compatibility with older cached data)
                    // Allow negative values for grace period:
                    // - Real-time arrivals: removed at -1 min (GPS is accurate)
                    // - Scheduled arrivals: removed at -2 min (account for boarding/padding)
                    return {
                        ...a,
                        minutes: a.minutes - 1 / 60,
                    };
                });
            }
        }, 1000);

        const alertTimer = setInterval(() => {
            currentAlertIndex = (currentAlertIndex + 1) % alerts.length;
        }, 6000);

        (async () => {
            // BUGFIX: Add proper error logging for device registration
            try {
                const fingerprint = await getDeviceFingerprint();
                if (!isUnmounted) {
                    await registerDevice(fingerprint);
                }
            } catch (e) {
                console.error('Device registration failed:', e);
                // Non-critical error, continue app initialization
            }

            // BUGFIX: Add proper error logging for config hydration
            try {
                const savedConfig = localStorage.getItem(CONFIG_STORAGE_KEY);
                if (savedConfig) {
                    try {
                        const parsed = JSON.parse(savedConfig);
                        if (!isUnmounted) {
                            isRestoringConfig = true;
                            updateConfig(parsed);
                            // EXPLICIT CASCADE: Trigger data loading for restored config
                            // This replaces the Location Cascade effect for initialization
                            if (parsed.country) {
                                reloadStates(parsed.country);
                                if (parsed.state) {
                                    reloadAgencies(parsed.country, parsed.state);
                                }
                            }
                        }
                    } catch (e) {
                        console.error('Failed to parse saved config:', e);
                        // Clear corrupted config
                        localStorage.removeItem(CONFIG_STORAGE_KEY);
                    } finally {
                        isRestoringConfig = false;
                    }
                }

                const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
                if (savedTheme === 'dark' || savedTheme === 'light') {
                    setTheme(savedTheme);
                }

                const savedTimeFormat = localStorage.getItem(TIME_FORMAT_KEY);
                if (savedTimeFormat === '24' || savedTimeFormat === '12') {
                    setTimeFormat(savedTimeFormat === '24');
                }

                const savedTempUnit = localStorage.getItem(TEMP_UNIT_KEY);
                if (savedTempUnit === 'C' || savedTempUnit === 'F') {
                    setTempUnit(savedTempUnit === 'C');
                }

                const savedLocale = localStorage.getItem(LOCALE_KEY);
                if (savedLocale) {
                    setLocale(savedLocale);
                }

                recentRoutes = loadRecent<Route>(RECENT_ROUTES_KEY);
                recentStops = loadRecent<Stop>(RECENT_STOPS_KEY);
            } catch (e) {
                console.error('Error loading saved settings:', e);
                // Non-critical error, continue with defaults
            }
            if (isUnmounted) return;
            hasHydratedConfig = true;

            // BUGFIX: Add error handling for initial data loading
            try {
                await reloadCountries();
            } catch (e) {
                console.error('Failed to load initial countries:', e);
                // Non-critical error, UI will show retry button
            }

            // FIXED: Use individual state variable instead of config.stop
            if (stop && !isUnmounted) {
                try {
                    await loadArrivals(stop);
                } catch (e) {
                    console.error('Failed to load initial arrivals:', e);
                    // Non-critical error, will retry via SSE or polling
                }
            }
        })().catch((err) => {
            console.error('Unhandled initialization error:', err);
        });

        return () => {
            isUnmounted = true;
            clearInterval(timer);
            clearInterval(alertTimer);
        };
    });

    // Real-time arrivals using Server-Sent Events (SSE)
    let lastSSEStop = '';
    let lastSSEDirection = '';
    $effect(() => {
        let eventSource: EventSource | null = null;
        let fallbackTimer: number | null = null;
        // P1-3: Adaptive polling variables
        let pollInterval = 30000; // Start at 30s
        let pollAttempts = 0;
        const MIN_POLL_INTERVAL = 30000; // 30s minimum
        const MAX_POLL_INTERVAL = 300000; // 5 min maximum
        let isPolling = false;
        // CRITICAL FIX: Abort flag to stop polling loop on cleanup
        let shouldAbortPolling = false;

        // CRITICAL: Read ALL needed values at the TOP of the effect
        // This captures them for use in callbacks without creating additional dependencies
        const currentStop = stop;
        const currentDirs = selectedDirections;
        const currentDirection = currentDirs.length > 0 ? currentDirs[0] : '';

        if (!currentStop) {
            transitData = null;
            lastSSEStop = '';
            lastSSEDirection = '';
            // BUGFIX: Ensure cleanup happens even when stop is cleared
            return () => {
                shouldAbortPolling = true; // Stop polling loop
                if (eventSource) {
                    eventSource.close();
                    eventSource = null;
                }
                if (fallbackTimer) {
                    clearTimeout(fallbackTimer);
                    fallbackTimer = null;
                }
                isPolling = false;
            };
        }

        // CRITICAL GUARD: Only run if stop or direction actually changed
        if (currentStop === lastSSEStop && currentDirection === lastSSEDirection) return;
        lastSSEStop = currentStop;
        lastSSEDirection = currentDirection;

        status = 'connecting';
        liveFeedMode = 'connecting';

        // P1-3: Adaptive polling function with exponential backoff
        // CRITICAL: Use captured currentStop, not the reactive 'stop' variable
        const startAdaptivePolling = () => {
            if (isPolling) return; // Prevent multiple polling loops
            isPolling = true;

            const poll = async () => {
                // CRITICAL: Check abort flag first to prevent memory leak
                if (shouldAbortPolling) {
                    console.log('Polling aborted by cleanup');
                    return;
                }

                // Don't poll when tab is hidden (Page Visibility API)
                if (document.hidden) {
                    console.log('Tab hidden, skipping poll');
                    fallbackTimer = setTimeout(poll, pollInterval) as unknown as number;
                    return;
                }

                try {
                    // CRITICAL: Use captured currentStop, NOT the reactive 'stop' variable
                    if (currentStop && !shouldAbortPolling) {
                        await loadArrivals(currentStop);
                        pollAttempts = 0;
                        pollInterval = MIN_POLL_INTERVAL; // Reset to 30s on success
                        console.log(`Poll succeeded, interval: ${pollInterval}ms`);
                    }
                } catch (error) {
                    pollAttempts++;
                    pollInterval = Math.min(pollInterval * 1.5, MAX_POLL_INTERVAL);
                    console.warn(`Poll failed (attempt ${pollAttempts}), backing off to ${pollInterval}ms`);
                }

                // CRITICAL: Check abort flag before scheduling next poll
                if (!shouldAbortPolling) {
                    fallbackTimer = setTimeout(poll, pollInterval) as unknown as number;
                }
            };

            poll(); // Start immediately
        };

        // Try SSE connection for real-time updates
        // CRITICAL: Use captured values, NOT reactive variables
        try {
            // Pass first direction to SSE (client-side filtering handles multiple)
            const directionParam = currentDirection || undefined;
            const sseUrl = directionParam
                ? `${API_BASE}/live/${currentStop}?direction=${encodeURIComponent(directionParam)}`
                : `${API_BASE}/live/${currentStop}`;
            eventSource = new EventSource(sseUrl);

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    transitData = data;
                    status = 'online';
                    liveFeedMode = 'live';
                    lastSuccessfulFetch = new Date(); // Track fresh SSE data
                } catch (err) {
                    console.error('SSE parse error:', err);
                }
            };

            eventSource.onerror = () => {
                console.warn('SSE connection failed, falling back to adaptive polling');
                eventSource?.close();
                status = 'offline';
                liveFeedMode = 'offline';

                // P1-3: Use adaptive polling instead of fixed interval
                if (!isPolling) {
                    startAdaptivePolling();
                }
            };

            eventSource.onopen = () => {
                status = 'connecting';
                liveFeedMode = 'connecting';
            };
        } catch (err) {
            console.error('Failed to establish SSE connection:', err);
            // P1-3: Immediate fallback to adaptive polling
            startAdaptivePolling();
        }

        // Cleanup function - CRITICAL FIX: Enhanced cleanup to prevent memory leaks
        return () => {
            // CRITICAL: Set abort flag FIRST to stop polling loop recursion
            shouldAbortPolling = true;

            if (eventSource) {
                // CRITICAL: Remove event handlers before closing to prevent memory leaks
                eventSource.onmessage = null;
                eventSource.onerror = null;
                eventSource.onopen = null;
                eventSource.close();
                eventSource = null;
            }
            if (fallbackTimer) {
                clearTimeout(fallbackTimer);
                fallbackTimer = null;
            }
            isPolling = false; // Reset polling flag to allow restart
        };
    });

    $effect(() => {
        if (typeof document === 'undefined') return;
        document.body.classList.toggle('theme-dark', theme === 'dark');
    });

    $effect(() => {
        if (typeof window === 'undefined') return;
        if (!isSettingsOpen) {
            if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
                queueMicrotask(() => lastFocusedElement?.focus());
            }
            return;
        }

        lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

        // Move focus into the modal for keyboard users.
        queueMicrotask(() => {
            countrySelectEl?.focus();
        });

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                isSettingsOpen = false;
                configError = '';
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    });

    // Fetch weather when stop coordinates are available
    // CRITICAL FIX: Add request tracking to prevent race conditions
    // Keep request ID non-reactive to avoid self-triggering the weather effect.
    let weatherRequestId = 0;

    $effect(() => {
        if (!stopCoordinates) {
            weatherData = null;
            return;
        }

        // Track this specific request
        const requestId = ++weatherRequestId;
        const { lat, lon } = stopCoordinates;

        fetchWeather(lat, lon)
            .then((data) => {
                // CRITICAL: Only update if this request is still current
                if (requestId !== weatherRequestId) {
                    console.log('Discarding stale weather response');
                    return;
                }
                weatherData = data;
                console.log('Weather fetched:', data);
            })
            .catch((error) => {
                // Only log error if request is still current
                if (requestId === weatherRequestId) {
                    console.error('Weather fetch failed:', error);
                    weatherData = null;
                }
            });
    });

    // NOTE: Location cascade logic has been moved to handleCountryChange and handleStateChange
    // for explicit control and to prevent effect loops

    let lastAgencyLoadKey = '';
    $effect(() => {
        const a = agency;
        const routesTick = routesReloadTick;
        const loadKey = `${a}:${routesTick}`;

        if (!a) {
            routesRequestId += 1;
            routes = [];
            routesError = null;
            lastAgencyLoadKey = '';
            loadingRoutes = false;
            return;
        }

        // CRITICAL GUARD: Only run if agency changed or an explicit retry was requested
        if (loadKey === lastAgencyLoadKey) return;
        lastAgencyLoadKey = loadKey;
        const requestId = ++routesRequestId;

        loadingRoutes = true;
        routesError = null;
        const cacheKey = `routes_cache_${a}`;
        let seededFromCache = false;
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (requestId === routesRequestId && Array.isArray(parsed)) {
                    const cachedRoutes = dedupeRoutes(parsed);
                    routes = cachedRoutes;
                    seededFromCache = cachedRoutes.length > 0;
                }
            }
        } catch (e) {}

        fetchRoutes(a)
            .then((data) => {
                if (requestId !== routesRequestId) return;
                routes = dedupeRoutes(data.routes || []);
                try {
                    localStorage.setItem(cacheKey, JSON.stringify(routes));
                } catch (e) {}
                console.log(`Loaded ${data.routes?.length || 0} routes for agency:`, a);
            })
            .catch((err) => {
                if (requestId !== routesRequestId) return;
                console.error('Error loading routes:', err);
                if (!seededFromCache) {
                    routes = [];
                    routesError = formatErrorMessage(err);
                } else {
                    routesError = 'Showing cached lines while live data reconnects.';
                }
            })
            .finally(() => {
                if (requestId === routesRequestId) {
                    loadingRoutes = false;
                }
            });
    });

    // NOTE: Using plain variable, not $state, to avoid triggering reactivity loops
    let lastRoute = '';
    $effect(() => {
        const r = route;
        if (r === lastRoute) return;
        lastRoute = r;
        if (!hasHydratedConfig) return;
        if (isRestoringConfig) return;

        invalidateStopsLoading();
        setDirectionsIfChanged([]);
        setStopsIfChanged([]);
        updateConfig({ directions: [], directionNames: [], stop: '', stopName: '' });
    });

    // Defensive invariant: route is the parent selection for directions/stops.
    // If route becomes empty for any reason, clear dependent selections immediately.
    $effect(() => {
        const currentRoute = route;
        const currentDirections = selectedDirections;
        const currentStop = stop;
        if (isRestoringConfig) return;
        if (currentRoute) return;
        if (currentDirections.length === 0 && !currentStop) return;
        updateConfig({ directions: [], directionNames: [], stop: '', stopName: '' });
        setDirectionsIfChanged([]);
        setStopsIfChanged([]);
    });

    // Track route changes with a key to prevent re-running when other variables change
    let lastDirectionsLoadKey = '';
    $effect(() => {
        // Read route inside to track it
        const r = route;
        const directionsTick = directionsReloadTick;
        const loadKey = `${r}:${directionsTick}`;

        if (!r) {
            directionsRequestId += 1;
            invalidateStopsLoading();
            setStopsIfChanged([]);
            setDirectionsIfChanged([]);
            directionsError = null;
            lastDirectionsLoadKey = '';
            loadingDirections = false;
            return;
        }

        // Only fetch directions if route changed or an explicit retry was requested
        if (loadKey === lastDirectionsLoadKey) return;
        lastDirectionsLoadKey = loadKey;
        const requestId = ++directionsRequestId;
        const isStaleDirectionsRequest = () => requestId !== directionsRequestId || route !== r;
        const directionsCacheKey = `directions_cache_v1_${r}`;
        let seededDirectionsFromCache = false;

        loadingDirections = true;
        directionsError = null;
        try {
            const cached = localStorage.getItem(directionsCacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed)) {
                    const cachedDirections = parsed
                        .filter((item) => item && typeof item.id === 'string' && typeof item.label === 'string')
                        .map((item) => ({ id: item.id, label: item.label, isDirId: !!item.isDirId }));
                    if (cachedDirections.length > 0 && !isStaleDirectionsRequest()) {
                        setDirectionsIfChanged(cachedDirections, true);
                        seededDirectionsFromCache = true;
                    }
                }
            }
        } catch (e) {}

        fetchTrips(r, true)
            .then(async (data) => {
                if (isStaleDirectionsRequest()) return;
                const trips = data.trips || [];
                // ... rest remains same ...

                // PRIORITY 1: Use headsigns from GTFS trips.txt
                // Display each unique headsign as what users see on the train/bus
                if (trips.length > 0) {
                    const nextDirections = trips.map((trip: Trip) => {
                        // Use headsign as both ID and label (what users see on vehicle)
                        const headsign = trip.trip_headsign;
                        const dirId = trip.direction_id;

                        // If no headsign, fall back to direction_id
                        if (!headsign) {
                            return {
                                id: String(dirId), // Use direction_id as fallback
                                label: `Direction ${dirId}`,
                                isDirId: true
                            };
                        }

                        return {
                            id: headsign, // Use headsign as ID (matches what's on the bus/train)
                            label: headsign,
                            isDirId: false // This is a headsign, not direction_id
                        };
                    });

                    // Use helper to prevent infinite loops
                    if (isStaleDirectionsRequest()) return;
                    setDirectionsIfChanged(nextDirections);
                    try {
                        localStorage.setItem(directionsCacheKey, JSON.stringify(nextDirections));
                    } catch (e) {}

                    console.log(`✅ Using ${trips.length} headsign(s) from GTFS`);
                    return;
                }

                // FALLBACK 1: Try to extract headsigns from real-time arrivals
                // Many agencies don't have trip_headsign in GTFS but DO have headsign in real-time data
                console.log('No GTFS headsigns found - attempting to extract from real-time arrivals...');
                try {
                    const stopsData = await fetchStops(r);
                    if (isStaleDirectionsRequest()) return;
                    const routeStops = stopsData.stops || [];

                    if (routeStops.length >= 2) {
                        const headsignsFromArrivals = new Set<string>();

                        // CRITICAL FIX: Use geographic coordinates to sample from terminals, not array indices
                        // fetchStops returns stops in ARBITRARY order, not route order
                        // We need to identify terminals by geographic position to capture both directions
                        const stopsWithCoords = routeStops.filter(s => s.geometry?.coordinates);
                        console.log(`Stops with coordinates: ${stopsWithCoords.length}`);

                        if (stopsWithCoords.length === 0) {
                            console.warn('No stops with coordinates found');
                        }

                        const stopIndicesToCheck: number[] = [];

                        if (stopsWithCoords.length > 0) {
                            // Calculate geographic bounds
                            let minLat = Infinity, maxLat = -Infinity;
                            let minLon = Infinity, maxLon = -Infinity;

                            stopsWithCoords.forEach(stop => {
                                const [lon, lat] = stop.geometry.coordinates;
                                minLat = Math.min(minLat, lat);
                                maxLat = Math.max(maxLat, lat);
                                minLon = Math.min(minLon, lon);
                                maxLon = Math.max(maxLon, lon);
                            });

                            const latRange = maxLat - minLat;
                            const lonRange = maxLon - minLon;

                            console.log(`Geographic bounds: lat [${minLat.toFixed(4)}, ${maxLat.toFixed(4)}], lon [${minLon.toFixed(4)}, ${maxLon.toFixed(4)}]`);
                            console.log(`Route orientation: ${latRange > lonRange ? 'north-south' : 'east-west'}`);

                            if (latRange > lonRange) {
                                // North-south route: sample from north and south terminals
                                const northTerminal = stopsWithCoords.reduce((north, stop) =>
                                    stop.geometry.coordinates[1] > north.geometry.coordinates[1] ? stop : north
                                );
                                const southTerminal = stopsWithCoords.reduce((south, stop) =>
                                    stop.geometry.coordinates[1] < south.geometry.coordinates[1] ? stop : south
                                );

                                const northIdx = routeStops.indexOf(northTerminal);
                                const southIdx = routeStops.indexOf(southTerminal);

                                stopIndicesToCheck.push(northIdx, southIdx);
                                console.log(`Sampling north terminal (idx ${northIdx}): ${northTerminal.stop_name}`);
                                console.log(`Sampling south terminal (idx ${southIdx}): ${southTerminal.stop_name}`);
                            } else {
                                // East-west route: sample from east and west terminals
                                const eastTerminal = stopsWithCoords.reduce((east, stop) =>
                                    stop.geometry.coordinates[0] > east.geometry.coordinates[0] ? stop : east
                                );
                                const westTerminal = stopsWithCoords.reduce((west, stop) =>
                                    stop.geometry.coordinates[0] < west.geometry.coordinates[0] ? stop : west
                                );

                                const eastIdx = routeStops.indexOf(eastTerminal);
                                const westIdx = routeStops.indexOf(westTerminal);

                                stopIndicesToCheck.push(eastIdx, westIdx);
                                console.log(`Sampling east terminal (idx ${eastIdx}): ${eastTerminal.stop_name}`);
                                console.log(`Sampling west terminal (idx ${westIdx}): ${westTerminal.stop_name}`);
                            }

                            // Also sample from geographic middle for additional coverage
                            const midLat = (minLat + maxLat) / 2;
                            const midLon = (minLon + maxLon) / 2;
                            const middleStop = stopsWithCoords.reduce((closest, stop) => {
                                const [lon, lat] = stop.geometry.coordinates;
                                const dist = Math.sqrt(Math.pow(lat - midLat, 2) + Math.pow(lon - midLon, 2));
                                const [closestLon, closestLat] = closest.geometry.coordinates;
                                const closestDist = Math.sqrt(Math.pow(closestLat - midLat, 2) + Math.pow(closestLon - midLon, 2));
                                return dist < closestDist ? stop : closest;
                            });

                            const middleIdx = routeStops.indexOf(middleStop);
                            if (!stopIndicesToCheck.includes(middleIdx)) {
                                stopIndicesToCheck.push(middleIdx);
                                console.log(`Sampling middle stop (idx ${middleIdx}): ${middleStop.stop_name}`);
                            }
                        } else {
                            // Fallback to array indices if no coordinates available
                            stopIndicesToCheck.push(0);
                            if (routeStops.length > 2) {
                                stopIndicesToCheck.push(Math.floor(routeStops.length / 2));
                            }
                            if (routeStops.length > 1) {
                                stopIndicesToCheck.push(routeStops.length - 1);
                            }
                        }

                        // Sample arrivals from multiple stops in parallel
                        const arrivalPromises = stopIndicesToCheck.map(async (index) => {
                            try {
                                const stop = routeStops[index];
                                const controller = new AbortController();
                                setTimeout(() => controller.abort(), 4000); // 4s timeout per stop

                                // CRITICAL FIX: Use 24-hour window to capture trips after midnight (next service day)
                                // At 11:44 PM, northbound trips starting after midnight need to be included
                                const arrivalsResponse = await fetchArrivals(stop.onestop_id, undefined, ARRIVALS.EXTENDED_WINDOW, controller.signal);
                                return arrivalsResponse.data?.arrivals || [];
                            } catch (error) {
                                console.warn(`Failed to fetch arrivals for stop ${routeStops[index]?.onestop_id}:`, error);
                                return [];
                            }
                        });

                        // Wait for all arrival requests to complete
                        const allArrivalsArrays = await Promise.all(arrivalPromises);

                        // Extract headsigns from all arrivals, filtering to only this route
                        // CRITICAL: Stops can serve multiple routes, so we must filter arrivals by routeOnestopId
                        let totalArrivals = 0;
                        let matchingArrivals = 0;
                        allArrivalsArrays.forEach((arrivals) => {
                            arrivals.forEach((arrival: Arrival) => {
                                totalArrivals++;
                                // Only include arrivals that belong to the current route
                                if (arrival.routeOnestopId === r && arrival.headsign && arrival.headsign.trim()) {
                                    headsignsFromArrivals.add(arrival.headsign.trim());
                                    matchingArrivals++;
                                }
                            });
                        });
                        console.log(`📊 Filtered ${matchingArrivals}/${totalArrivals} arrivals matching route ${r}`);

                        // If we found headsigns from arrivals, use them!
                        if (headsignsFromArrivals.size > 0) {
                            const realHeadsigns = Array.from(headsignsFromArrivals).sort();
                            const derivedHeadsignDirections = realHeadsigns.map((headsign) => ({
                                id: headsign,
                                label: headsign,
                                isDirId: false
                            }));
                            if (isStaleDirectionsRequest()) return;
                            setDirectionsIfChanged(derivedHeadsignDirections);
                            try {
                                localStorage.setItem(directionsCacheKey, JSON.stringify(derivedHeadsignDirections));
                            } catch (e) {}
                            console.log(`✅ Extracted ${realHeadsigns.length} headsigns from real-time arrivals by sampling ${stopIndicesToCheck.length} stops:`, realHeadsigns);
                            return;
                        }
                        console.log(`⚠️ No headsigns found in real-time arrivals after checking ${stopIndicesToCheck.length} stops - proceeding to terminal-based fallback`);
                    }
                } catch (error) {
                    console.warn('Failed to extract headsigns from arrivals:', error);
                }

                // FALLBACK 2: No headsigns available - use direction_id based logic
                const directionIds = normalizeList(
                    trips
                        .map((trip: Trip) => {
                            if (trip.direction_id === 1) return '1';
                            if (trip.direction_id === 0) return '0';
                            return null;
                        })
                        .filter(Boolean)
                );

                if (directionIds.length > 0) {
                    try {
                        if (isStaleDirectionsRequest()) return;
                        // FIXED: Use individual state variable instead of config.agency
                        console.log(`Agency ${agency} missing headsigns - using fallback hierarchy`);

                        // Fetch stops to determine route direction
                        const stopsData = await fetchStops(r);
                        if (isStaleDirectionsRequest()) return;
                        const routeStops = stopsData.stops || [];

                        if (routeStops.length >= 2) {
                            const firstStop = routeStops[0];
                            const lastStop = routeStops[routeStops.length - 1];
                            const firstCoords = firstStop.geometry?.coordinates;
                            const lastCoords = lastStop.geometry?.coordinates;

                            // STRATEGY 1: Use terminal stop names (most user-friendly)
                            if (firstStop.stop_name && lastStop.stop_name) {
                                // Check if route is circular (start ≈ end)
                                const isCircular = firstCoords && lastCoords &&
                                    Math.abs(firstCoords[0] - lastCoords[0]) < 0.001 &&
                                    Math.abs(firstCoords[1] - lastCoords[1]) < 0.001;

                                if (isCircular) {
                                    // Circular route: use clockwise/counterclockwise
                                    const circularDirs = [
                                        { id: '0', label: 'Clockwise', isDirId: false },
                                        { id: '1', label: 'Counterclockwise', isDirId: false }
                                    ];
                                    if (isStaleDirectionsRequest()) return;
                                    setDirectionsIfChanged(circularDirs);
                                    try {
                                        localStorage.setItem(directionsCacheKey, JSON.stringify(circularDirs));
                                    } catch (e) {}
                                    console.log(`✅ Circular route detected, using: ${circularDirs.map(d => d.label).join(', ')}`);
                                } else {
                                    // Linear route: use terminal names
                                    const terminalDirs = [
                                        { id: '0', label: `To ${lastStop.stop_name}`, isDirId: false },
                                        { id: '1', label: `To ${firstStop.stop_name}`, isDirId: false }
                                    ];
                                    if (isStaleDirectionsRequest()) return;
                                    setDirectionsIfChanged(terminalDirs);
                                    try {
                                        localStorage.setItem(directionsCacheKey, JSON.stringify(terminalDirs));
                                    } catch (e) {}
                                    console.log(`✅ Using terminal stop names: ${terminalDirs.map(d => d.label).join(', ')}`);
                                }
                            }
                            // STRATEGY 2: Calculate compass bearing (when terminal names unavailable)
                            else if (firstCoords && lastCoords) {
                                // Calculate bearing using proper geographic formula
                                const calculateBearing = (lat1: number, lon1: number, lat2: number, lon2: number) => {
                                    const φ1 = lat1 * Math.PI / 180;
                                    const φ2 = lat2 * Math.PI / 180;
                                    const Δλ = (lon2 - lon1) * Math.PI / 180;

                                    const y = Math.sin(Δλ) * Math.cos(φ2);
                                    const x = Math.cos(φ1) * Math.sin(φ2) -
                                              Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

                                    const θ = Math.atan2(y, x);
                                    return (θ * 180 / Math.PI + 360) % 360; // 0-360°
                                };

                                // Convert bearing to 8-directional compass label
                                const bearingToDirection = (bearing: number) => {
                                    if (bearing >= 337.5 || bearing < 22.5) return 'Northbound';
                                    if (bearing >= 22.5 && bearing < 67.5) return 'Northeastbound';
                                    if (bearing >= 67.5 && bearing < 112.5) return 'Eastbound';
                                    if (bearing >= 112.5 && bearing < 157.5) return 'Southeastbound';
                                    if (bearing >= 157.5 && bearing < 202.5) return 'Southbound';
                                    if (bearing >= 202.5 && bearing < 247.5) return 'Southwestbound';
                                    if (bearing >= 247.5 && bearing < 292.5) return 'Westbound';
                                    if (bearing >= 292.5 && bearing < 337.5) return 'Northwestbound';
                                    return 'Northbound';
                                };

                                const forwardBearing = calculateBearing(
                                    firstCoords[1], firstCoords[0],
                                    lastCoords[1], lastCoords[0]
                                );
                                const reverseBearing = (forwardBearing + 180) % 360;

                                const primaryDir = bearingToDirection(forwardBearing);
                                const oppositeDir = bearingToDirection(reverseBearing);

                                const compassDirs = [
                                    { id: '0', label: primaryDir, isDirId: false },
                                    { id: '1', label: oppositeDir, isDirId: false }
                                ];
                                if (isStaleDirectionsRequest()) return;
                                setDirectionsIfChanged(compassDirs);
                                try {
                                    localStorage.setItem(directionsCacheKey, JSON.stringify(compassDirs));
                                } catch (e) {}

                                console.log(`✅ Calculated compass directions: ${compassDirs.map(d => d.label).join(', ')}`);
                            } else {
                                // STRATEGY 3: Keep generic direction IDs
                                console.warn('Route missing coordinates and stop names, using generic labels');
                                if (isStaleDirectionsRequest()) return;
                                const genericDirections = directionIds.map(id => ({ id, label: `Direction ${id}`, isDirId: true }));
                                setDirectionsIfChanged(genericDirections);
                                try {
                                    localStorage.setItem(directionsCacheKey, JSON.stringify(genericDirections));
                                } catch (e) {}
                            }
                        } else {
                            console.warn('Route has < 2 stops, using generic labels');
                            if (isStaleDirectionsRequest()) return;
                            const genericDirections = directionIds.map(id => ({ id, label: `Direction ${id}`, isDirId: true }));
                            setDirectionsIfChanged(genericDirections);
                            try {
                                localStorage.setItem(directionsCacheKey, JSON.stringify(genericDirections));
                            } catch (e) {}
                        }
                    } catch (err) {
                        console.warn('Could not determine route directions:', err);
                        if (isStaleDirectionsRequest()) return;
                        const genericDirections = directionIds.map(id => ({ id, label: `Direction ${id}`, isDirId: true }));
                        setDirectionsIfChanged(genericDirections);
                        try {
                            localStorage.setItem(directionsCacheKey, JSON.stringify(genericDirections));
                        } catch (e) {}
                    }
                } else {
                    // No headsigns and no direction IDs - empty route
                    console.warn('Route has no trips with headsigns or direction_ids');
                    if (isStaleDirectionsRequest()) return;
                    setDirectionsIfChanged([]);
                }

                // FIXED: Log when no directions found to help diagnose issues
                if (isStaleDirectionsRequest()) return;
                if (directions.length === 0) {
                    console.warn('No directions found for route:', r);
                }
            })
            .catch((error) => {
                if (requestId !== directionsRequestId) return;
                // FIXED: Add error handling for trips fetch failures
                console.error('Failed to fetch directions:', error);
                if (!seededDirectionsFromCache) {
                    setDirectionsIfChanged([]);
                    directionsError = formatErrorMessage(error);
                } else {
                    directionsError = 'Showing cached destinations while live data reconnects.';
                }
            })
            .finally(() => {
                if (requestId === directionsRequestId) {
                    loadingDirections = false;
                    if (route !== r) return;
                    // Cache-seeded single-destination routes can skip auto-select if
                    // the fetched direction list matches cache exactly. Force the
                    // same explicit auto-select path once loading has completed.
                    if (directions.length === 1 && selectedDirections.length === 0 && !userHasInteractedWithDirections) {
                        autoSelectDirectionIfNeeded();
                        return;
                    }
                    // Keep stop options in sync when direction loading finishes:
                    // - if there are no destinations for this line, load all stops
                    // - if a destination is already selected (e.g. restored config), load filtered stops
                    if (directions.length === 0 || selectedDirections.length > 0) {
                        loadStopsForCurrentSelection();
                    }
                }
            });
    });

    // NOTE: STOPS loading is now handled explicitly by loadStopsForCurrentSelection()
    // Called from handleDirectionToggle and autoSelectDirectionIfNeeded

    // NOTE: Auto-select direction is now handled explicitly by autoSelectDirectionIfNeeded()
    // Called from the directions fetch .then() callback


    // NOTE: Using plain variable to track last validated stops key
    let lastValidatedStopsKey = '';
    $effect(() => {
        // Capture stops outside untrack so we track it
        const currentStops = stops;
        // CRITICAL: Skip validation while stops are still loading to prevent loop
        // Use untrack to avoid creating dependency on loadingStops
        const isLoading = untrack(() => loadingStops);
        if (isLoading) return;

        if (!currentStops.length) {
            lastValidatedStopsKey = '';
            untrack(() => {
                if (stop) {
                    updateConfig({ stop: '', stopName: '' });
                }
            });
            return;
        }

        // CRITICAL: Only validate once per unique stops set
        const stopsKey = currentStops.map(s => s.onestop_id).join('|');
        if (stopsKey === lastValidatedStopsKey) return;
        lastValidatedStopsKey = stopsKey;

        // Use untrack to prevent infinite loop when reading/writing stop
        untrack(() => {
            if (!stop) return;
            const exists = currentStops.some((s) => s.onestop_id === stop);
            if (!exists) {
                updateConfig({ stop: '', stopName: '' });
            }
        });
    });

    $effect(() => {
        if (alerts.length > 0 && currentAlertIndex >= alerts.length && currentAlertIndex !== 0) {
            currentAlertIndex = 0;
        }
    });

    // REMOVED: Redundant effect that duplicated arrival loading
    // The SSE effect at line 790 already handles direction changes by monitoring config.directions
    // and creating a new SSE connection with the updated direction parameter

    // Extended arrivals effect - ONLY depends on stop and selectedDirections
    // Uses untrack to read derived values without creating dependencies
    $effect(() => {
        // CRITICAL: Only track stop and selectedDirections to trigger this effect
        const currentStop = stop;
        const currentDirs = selectedDirections;

        // Use untrack for derived values to avoid re-running when transitData updates
        const currentFilteredCount = untrack(() => filteredArrivals.length);

        if (!currentStop) {
            extendedArrivals = [];
            lastExtendedKey = '';
            // Cancel any in-flight extended request
            if (currentExtendedController) {
                currentExtendedController.abort();
                currentExtendedController = null;
            }
            return;
        }

        // Check if we need extended arrivals (read without tracking)
        const needsExtended = currentFilteredCount < 4;

        if (!needsExtended) {
            // We have enough arrivals, clear extended data
            extendedArrivals = [];
            if (currentExtendedController) {
                currentExtendedController.abort();
                currentExtendedController = null;
            }
            return;
        }

        // Intentionally avoid backend direction filter here so scheduled fallback trips
        // with slight headsign differences can still be considered client-side.
        const key = `${currentStop}:extended_all`;
        if (key === lastExtendedKey) return;
        lastExtendedKey = key;
        lastExtendedWindow = ARRIVALS.EXTENDED_WINDOW;

        // FIXED: Cancel previous extended request before starting new one
        if (currentExtendedController) {
            currentExtendedController.abort();
        }
        currentExtendedController = new AbortController();
        const signal = currentExtendedController.signal;

        // Fetch with 24-hour window to ensure we can always show 3 upcoming trips
        // This includes live and scheduled trips, even if they're on the next service day
        fetchArrivals(currentStop, undefined, ARRIVALS.EXTENDED_WINDOW, signal)
            .then((result) => {
                // CRITICAL: Check if this request is still valid by comparing keys
                if (lastExtendedKey !== key) {
                    console.log('Extended arrivals request outdated (24h), ignoring');
                    return;
                }

                extendedArrivals = result.data?.arrivals || [];
                currentExtendedController = null;
            })
            .catch((err) => {
                // Ignore abort errors
                if (err instanceof Error && err.name === 'AbortError') {
                    console.log('Extended arrivals fetch aborted');
                    return;
                }
                // CRITICAL: Check before updating state on error
                if (lastExtendedKey !== key) {
                    return;
                }
                extendedArrivals = [];
                currentExtendedController = null;
            });
    });

    // Extract directions from transitData arrivals (fallback when GTFS has no directions)
    let lastTransitDataTimestamp = '';
    $effect(() => {
        // Read these first to exit early and avoid tracking transitData unnecessarily
        const r = route;
        const currentDirs = directions;
        const currentSelectedRoute = selectedRoute;
        // Use untrack to avoid creating dependency on loadingDirections
        const dirsLoading = untrack(() => loadingDirections);

        if (!r || currentDirs.length > 0 || dirsLoading) return;

        // Now read transitData
        const arrivals = transitData?.arrivals;
        if (!arrivals?.length) return;

        // Guard against stale data from a previously selected route.
        // Only derive directions from arrivals that match the current route.
        const matchingArrivals = arrivals.filter((arrival) => {
            if (arrival.routeOnestopId) {
                return arrival.routeOnestopId === r;
            }
            if (currentSelectedRoute?.route_short_name && arrival.routeShortName) {
                return arrival.routeShortName === currentSelectedRoute.route_short_name;
            }
            if (currentSelectedRoute?.route_long_name && arrival.routeLongName) {
                return arrival.routeLongName === currentSelectedRoute.route_long_name;
            }
            return false;
        });
        if (!matchingArrivals.length) return;

        // CRITICAL: Only extract once per unique transitData
        const dataKey = `${r}:${transitData.timestamp || ''}`;
        if (dataKey === lastTransitDataTimestamp) return;
        lastTransitDataTimestamp = dataKey;

        untrack(() => {
            const nextDirections = normalizeList(
                matchingArrivals
                    .map((arrival) => {
                        if (arrival.headsign) return arrival.headsign;
                        if (arrival.directionId === 1) return '1';
                        if (arrival.directionId === 0) return '0';
                        return null;
                    })
                    .filter(Boolean)
            ).map((value) => {
                if (value === '0') return { id: '0', label: 'Outbound' };
                if (value === '1') return { id: '1', label: 'Inbound' };
                return { id: value, label: value };
            });

            if (nextDirections.length) {
                setDirectionsIfChanged(nextDirections);
            }
        });
    });


    // NOTE: Auto-select stop is now handled explicitly by autoSelectStopIfNeeded()
    // Called from loadStopsForCurrentSelection's .finally() callback
</script>

<main class="kiosk h-screen w-screen flex flex-col gap-6 px-10 py-8 overflow-hidden select-none">
    <header class="flex items-center justify-between panel-rise" style="animation-delay: 40ms;">
        <div class="flex items-center gap-5">
            <button onclick={() => (isSettingsOpen = true)} class="logo-button" aria-label="Change agency">
                {#if logoUrl}
                    <img
                        src={logoUrl}
                        alt={selectedAgency ? formatAgencyName(selectedAgency) : 'Agency logo'}
                        onerror={() => {
                            logoUrl = '';
                        }}
                    />
                {:else}
                    <span class="logo-fallback" aria-hidden="true">
                        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
                            <circle cx="32" cy="32" r="24" stroke="currentColor" stroke-width="6" />
                            <rect x="22" y="20" width="20" height="6" rx="3" fill="currentColor" />
                            <rect x="29" y="26" width="6" height="22" rx="3" fill="currentColor" />
                        </svg>
                    </span>
                {/if}
            </button>
            <div class="space-y-2">
                <div class="text-5xl font-semibold tracking-tight text-slate-900">
                    {currentTime.toLocaleTimeString(locale, {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: !use24Hour,
                    })}
                </div>
                <div class="text-sm font-medium text-slate-500 tracking-[0.2em] uppercase space-y-1">
                    <div>
                        {currentTime.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                </div>
            </div>
        </div>
            <div class="glass-card flex items-center gap-6 px-6 py-3 rounded-full">
                <div class="flex items-center gap-2 text-sm font-semibold text-slate-600">
                    <Wifi size={18} class={status === 'online' ? 'text-emerald-500' : 'text-rose-500'} />
                    <span>
                        {#if liveFeedMode === 'live'}
                            Live
                        {:else if liveFeedMode === 'scheduled'}
                            Offline – Scheduled
                        {:else if status === 'connecting'}
                            Connecting…
                        {:else}
                            Offline
                        {/if}
                    </span>
                </div>
                <div class="w-px h-4 bg-slate-200"></div>
                <button
                    onclick={refreshCurrentSelection}
                    class="refresh-button"
                    aria-label="Refresh current data"
                    title="Refresh current data"
                    disabled={isRefreshingSelection}
                >
                    <RefreshCw size={16} />
                </button>
                {#if weatherData?.current}
                    {@const WeatherIcon = getWeatherIcon(weatherData.current.weather_code)}
                    <div class="w-px h-4 bg-slate-200"></div>
                    <div class="weather-widget">
                        <WeatherIcon size={18} class="weather-icon" />
                        <span class="weather-temp">{formatTemp(weatherData.current.temperature_2m)}</span>
                    </div>
                {/if}
                <div class="w-px h-4 bg-slate-200"></div>
                <!-- P1-5: ARIA label for theme toggle -->
                <button
                    onclick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    class="theme-toggle"
                    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                    aria-checked={theme === 'dark'}
                    role="switch"
                >
                {#if theme === 'dark'}
                    <Sun size={18} aria-hidden="true" />
                    <span>Light</span>
                {:else}
                    <Moon size={18} aria-hidden="true" />
                    <span>Dark</span>
                {/if}
            </button>
        </div>
    </header>

    <div class="flex-1 grid grid-cols-12 gap-6 min-h-0 items-stretch">
        <div
            class="col-span-8 h-full glass-card rounded-[2.5rem] p-10 flex flex-col justify-between relative overflow-hidden panel-rise main-clickable"
            style="animation-delay: 120ms;"
            role="button"
            tabindex="0"
            aria-label="Open settings to configure transit agency, route, and stop"
            onclick={() => (isSettingsOpen = true)}
            onkeydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') isSettingsOpen = true;
            }}
        >
            <div class="space-y-6 z-10">
                <!-- P1-5: ARIA live region for countdown -->
                <div class="flex flex-col gap-3">
                    <div
                        class="flex items-baseline gap-6"
                        role="timer"
                        aria-live="polite"
                        aria-atomic="true"
                        aria-label="Next arrival countdown"
                    >
                        <span class="text-[10.5rem] font-semibold leading-[0.85] text-slate-900 tracking-tight">
                            {#if liveFeedMode === 'connecting'}
                                <Loader2 class="animate-spin text-slate-300" size={72} />
                            {:else}
                                {mainEta.value}
                            {/if}
                        </span>
                        {#if liveFeedMode !== 'connecting' && !mainEta.isHours}
                            <span class="text-5xl font-semibold text-slate-300 uppercase tracking-widest">{mainEta.unit}</span>
                        {/if}
                    </div>
                    {#if primaryArrival}
                        <div class="text-lg font-medium text-slate-400 tracking-wide">
                            Arrives {formatArrivalTime(primaryArrival.minutes)}
                        </div>
                    {/if}
                </div>

                <div class="flex items-center gap-4">
                    <div
                        class="inline-flex items-center gap-3 px-8 py-3 rounded-full text-2xl font-semibold tracking-wide shadow-lg route-pill"
                        style={routeAccent ? `background:${routeAccent}; color:#fff;` : ''}
                    >
                        {routeDisplayName}
                    </div>
                </div>

                <div class="space-y-2">
                    <h2 class="text-5xl font-semibold text-slate-900 tracking-tight max-w-2xl">
                        {stopDisplayName}
                    </h2>
                    <p class="text-xl font-medium text-slate-500 tracking-wide">
                        {primaryDirectionLabel}
                    </p>
                    {#if formatLoadFactor(primaryArrival?.loadFactor)}
                        <span class="load-chip">
                            Load {formatLoadFactor(primaryArrival?.loadFactor)}
                        </span>
                    {/if}
                </div>
            </div>

            <!-- P1-6: Error State UI -->
            {#if lastError && (!transitData || !transitData.arrivals.length)}
                <div class="error-state" role="alert">
                    <svg class="error-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <h3 class="error-title">Unable to Load Arrivals</h3>
                    <p class="error-message">{lastError}</p>
                    <div class="error-actions">
                        <button
                            onclick={() => {
                                lastError = null;
                                refreshCurrentSelection();
                            }}
                            class="retry-button"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M13.65 2.35A7.96 7.96 0 008 0C3.58 0 0 3.58 0 8s3.58 8 8 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z"/>
                            </svg>
                            Retry Connection
                        </button>
                        <button
                            onclick={() => (isSettingsOpen = true)}
                            class="settings-error-button"
                        >
                            Open Settings
                        </button>
                    </div>
                </div>
            {/if}

            <div class="flex items-center justify-between z-10">
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-3 text-sm font-semibold text-slate-500">
                        {#if liveFeedMode === 'live'}
                            <span class="flex items-center gap-2">
                                <span class="relative flex h-2 w-2">
                                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                </span>
                                Live updates
                            </span>
                        {:else if lastUpdated}
                            Last updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {:else}
                            Waiting for live updates
                        {/if}
                    </div>
                    {#if dataFreshness === 'stale' || dataFreshness === 'very_stale'}
                        <span class="freshness-indicator {dataFreshness}" title="Data may be outdated">
                            {dataFreshness === 'very_stale' ? 'Stale data (10+ min)' : 'Data aging (3+ min)'}
                        </span>
                    {/if}
                    {#if liveFeedMode === 'live' && (dataConfidence === 'low' || dataConfidence === 'medium')}
                        <span class="confidence-indicator {dataConfidence}" title={dataConfidence === 'low' ? 'Scheduled times only' : 'Mixed real-time and scheduled'}>
                            {dataConfidence === 'low' ? 'Scheduled' : 'Partial real-time'}
                        </span>
                    {/if}
                </div>

            </div>
        </div>

        <aside class="col-span-4 h-full flex flex-col min-h-0 sidebar-stack">
            <div class="sidebar-cards sidebar-cards-fill">
                {#each sidebarArrivals as arrival, i}
                        <div class="glass-card rounded-[2rem] p-7 flex flex-col justify-between gap-5 panel-rise sidebar-card sidebar-fill" style={`animation-delay: ${200 + i * 80}ms;`}>
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-3 text-base font-semibold tracking-widest text-slate-500 uppercase">
                                    <span>{arrival.vehicle}</span>
                                    {#if arrival.headsign}
                                        <span class="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em]">
                                            {arrival.headsign}
                                        </span>
                                    {/if}
                                </div>
                                <div class="flex items-center gap-2">
                                    {#if arrival.isRealtime}
                                        <span class="arrival-type-badge live">LIVE</span>
                                    {:else}
                                        <span class="arrival-type-badge scheduled">SCHED</span>
                                    {/if}
                                    {#if arrival.status !== 'On Time'}
                                        <AlertTriangle size={20} class="text-amber-500" />
                                    {/if}
                                </div>
                            </div>
                            <div class="flex items-baseline justify-between">
                                <div class="flex flex-col gap-1">
                                    <div class="text-5xl font-semibold text-slate-900 tracking-tight">
                                        {formatDuration(arrival.minutes).value}
                                        {#if !formatDuration(arrival.minutes).isHours}
                                            <span class="text-2xl text-slate-300 ml-2">{formatDuration(arrival.minutes).unit}</span>
                                        {/if}
                                    </div>
                                    <div class="text-sm font-medium text-slate-400 tracking-wide">
                                        Arrives {formatArrivalTime(arrival.minutes)}
                                    </div>
                                </div>
                                <div class="flex flex-col items-end gap-2">
                                    <span class="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em]">
                                        {arrival.status}
                                    </span>
                                    {#if formatLoadFactor(arrival.loadFactor)}
                                        <span class="load-chip subtle">
                                            {formatLoadFactor(arrival.loadFactor)}
                                        </span>
                                    {/if}
                                </div>
                            </div>
                        </div>
                {/each}
                {#if sidebarArrivals.length === 0 && primaryArrival}
                    <div class="glass-card rounded-[2rem] p-10 flex flex-col items-center justify-center text-center text-slate-400 gap-4 panel-rise sidebar-card sidebar-fill" style="animation-delay: 200ms;">
                        <AlertTriangle size={28} class="text-slate-400" />
                        <span class="text-xs font-semibold uppercase tracking-[0.3em]">No more departures</span>
                        <span class="text-sm text-slate-500">
                            Showing the next arrival in the main panel.
                        </span>
                    </div>
                {:else if sidebarArrivals.length === 0 && liveFeedMode === 'connecting'}
                    <div class="glass-card rounded-[2rem] p-10 flex flex-col items-center justify-center text-center text-slate-400 gap-4 panel-rise sidebar-card sidebar-fill" style="animation-delay: 200ms;">
                        <Loader2 class="animate-spin text-slate-300" size={48} />
                        <span class="text-xs font-semibold uppercase tracking-[0.3em]">Connecting to live feed</span>
                    </div>
                {:else if sidebarArrivals.length === 0}
                    <div class="glass-card rounded-[2rem] p-10 flex flex-col items-center justify-center text-center text-slate-400 gap-4 panel-rise sidebar-card sidebar-fill" style="animation-delay: 200ms;">
                        <AlertTriangle size={32} class="text-amber-500" />
                        <span class="text-xs font-semibold uppercase tracking-[0.3em]">No upcoming departures</span>
                        <button class="secondary-button" onclick={refreshCurrentSelection}>
                            Retry
                        </button>
                    </div>
                {/if}
            </div>
        </aside>
    </div>

    <footer class="glass-card rounded-[2rem] flex items-center px-8 py-5 relative overflow-hidden panel-rise" style="animation-delay: 260ms;">
        <button class="flex items-center gap-4 text-slate-500 mr-6" onclick={() => (showAlerts = true)}>
            <AlertTriangle size={22} class="text-amber-500" />
            <span class="text-xs font-semibold uppercase tracking-[0.3em]">Service Alert</span>
        </button>
        <div class="flex-1 relative h-full flex items-center">
            {#key currentAlertIndex}
                <div
                    in:fly={{ y: 12, duration: 400 }}
                    out:fly={{ y: -12, duration: 400 }}
                    class="absolute inset-0 flex items-center"
                >
                    <span class="text-lg font-medium text-slate-700 tracking-wide">
                        {alerts[currentAlertIndex]}
                    </span>
                </div>
            {/key}
        </div>
        <div class="flex gap-2 ml-4">
            {#each alerts as _, i}
                <div class="w-2 h-2 rounded-full transition-all duration-500 {i === currentAlertIndex ? 'bg-slate-900 w-4' : 'bg-slate-200'}"></div>
            {/each}
        </div>
    </footer>

    {#if showAlerts}
        <div class="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-8">
            <div class="glass-card w-full max-w-2xl rounded-[2rem] overflow-hidden shadow-2xl">
                <div class="px-8 py-6 border-b border-slate-200 flex items-center justify-between">
                    <h3 class="text-xl font-semibold text-slate-900">Service Alerts</h3>
                    <button onclick={() => (showAlerts = false)} class="close-button" aria-label="Close alerts">
                        <X size={20} aria-hidden="true" />
                    </button>
                </div>
                <div class="p-8 space-y-4">
                    {#each alerts as alert}
                        <div class="alert-card">
                            <AlertTriangle size={18} class="text-amber-500" />
                            <span class="text-sm font-medium text-slate-700">{alert}</span>
                        </div>
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    {#if activationToast}
        <div class="toast">
            {activationToast}
        </div>
    {/if}

    {#if isSettingsOpen}
        <div class="fixed inset-0 bg-slate-900/90 z-50 flex items-center justify-center p-8">
            <button
                class="absolute inset-0"
                type="button"
                aria-label="Close settings"
                onclick={() => { isSettingsOpen = false; configError = ''; }}
            ></button>
            <div
                class="glass-card relative w-full max-w-3xl rounded-[2.5rem] overflow-hidden shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-label="Transit stop configuration"
                tabindex="-1"
            >
                <div class="px-10 py-8 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h3 class="text-2xl font-semibold text-slate-900">Select Stop</h3>
                    </div>
                    <button onclick={() => (isSettingsOpen = false)} class="close-button" aria-label="Close settings">
                        <X size={22} aria-hidden="true" />
                    </button>
                </div>

                <div class="p-10 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    <div class="grid grid-cols-2 gap-6">
                        <div class="space-y-2">
                            <label for="country-select" class="field-label">
                                Country {#if loadingCountries}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            <div class="select-wrapper">
                                <select id="country-select" bind:this={countrySelectEl} bind:value={country} onchange={(e) => handleCountryChange(e.currentTarget.value)} class="field-select">
                                    <option value="">Select country</option>
                                    {#if country && !countryExistsInOptions}
                                        <option value={country}>{country}</option>
                                    {/if}
                                    {#if !countries.length}
                                        <option value="" disabled>No countries available</option>
                                    {:else}
                                        {#each countries as countryItem (countryItem)}
                                            <option value={countryItem}>{countryItem}</option>
                                        {/each}
                                    {/if}
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                            {#if agenciesError}
                                <div class="text-xs font-medium text-rose-500">
                                    {agenciesError}
                                    <button onclick={retryLocationLoad} class="ml-2 underline">Retry</button>
                                </div>
                            {/if}
                            {#if usingFallbackCountries}
                                <div class="text-xs font-medium text-amber-500">
                                    Showing fallback countries while Transitland loads.
                                </div>
                            {/if}
                        </div>

                        <div class="space-y-2">
                            <label for="state-select" class="field-label">
                                State / Region {#if loadingStates}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            <div class="select-wrapper">
                                <select id="state-select" bind:this={stateSelectEl} bind:value={state} disabled={!country || loadingStates} onchange={(e) => handleStateChange(e.currentTarget.value)} class="field-select {loadingStates ? 'loading' : ''}">
                                    <option value="">Select state</option>
                                    {#if state && !stateExistsInOptions}
                                        <option value={state}>{state}</option>
                                    {/if}
                                    {#each states as stateItem (stateItem)}
                                        <option value={stateItem}>{stateItem}</option>
                                    {/each}
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                        </div>

                        <div class="space-y-2">
                            <label for="agency-select" class="field-label">
                                Agency {#if loadingAgencies}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            <div class="select-wrapper">
                                <select
                                    id="agency-select"
                                    bind:this={agencySelectEl}
                                    bind:value={agency}
                                    disabled={!state || loadingAgencies}
                                    onchange={(e) => handleAgencyChange(e.currentTarget.value)}
                                    class="field-select {loadingAgencies ? 'loading' : ''}"
                                >
                                    <option value="">{loadingAgencies ? 'Loading agencies...' : 'Select agency'}</option>
                                    {#if agency && !agencyExistsInOptions}
                                        <option value={agency}>{agencyName || agency}</option>
                                    {/if}
                                    {#each getSortedAgencies() as agencyItem (agencyItem.onestop_id)}
                                        <option value={agencyItem.onestop_id}>{formatAgencyName(agencyItem)}</option>
                                    {/each}
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                            {#if !loadingAgencies && state && agencyMatchCount === 0}
                                <div class="text-xs font-medium text-amber-500">
                                    No agencies found for this location. Try a nearby state/region.
                                </div>
                            {/if}
                        </div>

                        <div class="space-y-2">
                            <label for="route-select" class="field-label">
                                Line {#if loadingRoutes}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            <div class="select-wrapper">
                                <select
                                    id="route-select"
                                    bind:this={routeSelectEl}
                                    bind:value={route}
                                    disabled={!agency || loadingRoutes}
                                    onchange={(e) => handleRouteChange(e.currentTarget.value)}
                                    class="field-select {loadingRoutes ? 'loading' : ''}"
                                >
                                    <option value="">{loadingRoutes ? 'Loading lines...' : 'Select line'}</option>
                                    {#if route && !routeExistsInOptions}
                                        <option value={route}>{routeName || route}</option>
                                    {/if}
                                    {#if recentRouteOptions.length}
                                        <optgroup label="Recent">
                                            {#each recentRouteOptions as recentRoute (recentRoute.onestop_id)}
                                                <option value={recentRoute.onestop_id}>{formatRouteName(recentRoute)}</option>
                                            {/each}
                                        </optgroup>
                                    {/if}
                                    {#each groupedRoutes as group (group.label)}
                                        <optgroup label={group.label}>
                                            {#each group.routes as groupedRoute (groupedRoute.onestop_id)}
                                                <option value={groupedRoute.onestop_id}>{formatRouteName(groupedRoute)}</option>
                                            {/each}
                                        </optgroup>
                                    {/each}
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                            {#if routesError}
                                <div class="text-xs font-medium text-rose-500">
                                    {routesError}
                                    <button onclick={retryRoutesLoad} class="ml-2 underline">Retry</button>
                                </div>
                            {/if}
                        </div>

                        <div class="space-y-2 col-span-2">
                            <label class="field-label">
                                Destination(s) {#if loadingDirections}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            {#if directions.length === 1}
                                <div class="single-option">
                                    {directions[0].label}
                                </div>
                            {:else if directions.length === 0}
                                <div class="text-sm text-slate-400">
                                    {#if loadingDirections}
                                        Loading directions...
                                    {:else if !route}
                                        Select a line first
                                    {:else if stops.length > 0}
                                        No destinations available for this line. Choose a stop directly.
                                    {:else}
                                        No destinations available
                                    {/if}
                                </div>
                            {:else}
                                <div class="checkbox-group">
                                    {#each directions as direction}
                                        <label class="checkbox-label {selectedDirections.includes(direction.id) ? 'selected' : ''}">
                                            <input
                                                type="checkbox"
                                                checked={selectedDirections.includes(direction.id)}
                                                onchange={() => handleDirectionToggle(direction.id, direction.label)}
                                                disabled={loadingDirections}
                                            />
                                            <span>{direction.label}</span>
                                        </label>
                                    {/each}
                                </div>
                            {/if}
                            {#if directionsError}
                                <div class="text-xs font-medium text-rose-500 mt-2">
                                    {directionsError}
                                    <button onclick={retryDirectionsLoad} class="ml-2 underline">Retry</button>
                                </div>
                            {/if}
                        </div>

                        <div class="space-y-2 col-span-2">
                            <label for="stop-select" class="field-label">
                                Stop {#if loadingStops || loadingDirections}<Loader2 size={12} class="animate-spin text-slate-400" />{/if}
                            </label>
                            <div class="select-wrapper">
                                <select
                                    id="stop-select"
                                    bind:this={stopSelectEl}
                                    bind:value={stop}
                                    disabled={!route || loadingDirections || loadingStops || (directions.length > 0 && selectedDirections.length === 0)}
                                    onchange={(e) => handleStopChange(e.currentTarget.value)}
                                    class="field-select {(loadingStops || loadingDirections) ? 'loading' : ''}"
                                >
                                    <option value="">
                                        {#if loadingStops || loadingDirections}
                                            Loading stops...
                                        {:else}
                                            {stopsDisabledReason || 'Select stop'}
                                        {/if}
                                    </option>
                                    {#if stop && !stopExistsInOptions}
                                        <option value={stop}>{stopName || stop}</option>
                                    {/if}
                                    {#if recentStopOptions.length}
                                        <optgroup label="Recent">
                                            {#each recentStopOptions as recentStop (recentStop.onestop_id)}
                                                <option value={recentStop.onestop_id}>{formatStopName(recentStop)}</option>
                                            {/each}
                                        </optgroup>
                                    {/if}
                                    {#each stops as routeStop (routeStop.onestop_id)}
                                        <option value={routeStop.onestop_id}>{formatStopName(routeStop)}</option>
                                    {/each}
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>

                            <!-- Helper hint when stops are disabled -->
                            {#if stopsDisabledReason}
                                <div class="field-hint warning">
                                    <AlertTriangle size={14} />
                                    <span>{stopsDisabledReason}</span>
                                </div>
                            {/if}
                            {#if stopsError}
                                <div class="text-xs font-medium text-rose-500">
                                    {stopsError}
                                    <button onclick={retryStopsLoad} class="ml-2 underline">Retry</button>
                                </div>
                            {/if}
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-6">
                        <div class="space-y-2">
                            <label for="time-format-select" class="field-label">Time Format</label>
                            <div class="select-wrapper">
                                <select
                                    id="time-format-select"
                                    class="field-select"
                                    value={use24Hour ? '24' : '12'}
                                    onchange={(e) => setTimeFormat(e.currentTarget.value === '24')}
                                >
                                    <option value="12">12-hour</option>
                                    <option value="24">24-hour</option>
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                        </div>
                        <div class="space-y-2">
                            <label for="temp-unit-select" class="field-label">Temperature Unit</label>
                            <div class="select-wrapper">
                                <select
                                    id="temp-unit-select"
                                    class="field-select"
                                    value={useCelsius ? 'C' : 'F'}
                                    onchange={(e) => setTempUnit(e.currentTarget.value === 'C')}
                                >
                                    <option value="C">Celsius (°C)</option>
                                    <option value="F">Fahrenheit (°F)</option>
                                </select>
                                <ChevronDown size={18} class="select-icon" />
                            </div>
                        </div>
                    </div>

                    {#if configError}
                        <div class="text-sm font-medium text-rose-500">{configError}</div>
                    {/if}
                    <div class="flex items-center gap-4">
                        <button onclick={() => { isSettingsOpen = false; configError = ''; }} class="secondary-button flex-1">
                            Cancel
                        </button>
                        <button
                            onclick={refreshCurrentSelection}
                            disabled={!stop || isRefreshingSelection}
                            class="secondary-button flex-1"
                        >
                            {isRefreshingSelection ? 'Loading...' : 'Load'}
                        </button>
                        <button onclick={activateKiosk} class="primary-button flex-1">
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    {/if}
</main>

<style>
    :global(body) {
        margin: 0;
        padding: 0;
        background: #f5f5f7;
        color: #0f172a;
        font-family: "SF Pro Display", "SF Pro Text", "Neue Haas Grotesk", "Avenir Next", "Helvetica Neue", sans-serif;
        touch-action: manipulation;
        cursor: none;
        -webkit-tap-highlight-color: transparent;
    }

    .kiosk {
        background: radial-gradient(circle at 20% 10%, rgba(255, 255, 255, 0.9), transparent 55%),
            radial-gradient(circle at 80% 30%, rgba(229, 231, 235, 0.8), transparent 45%),
            linear-gradient(135deg, #f8fafc 0%, #eef2f7 50%, #f8fafc 100%);
    }

    .glass-card {
        background: rgba(255, 255, 255, 0.94);
        border: 1px solid rgba(15, 23, 42, 0.1);
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
        will-change: transform, opacity;
    }

    .logo-button {
        width: 120px;
        height: 120px;
        border-radius: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        color: #0f172a;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        box-shadow: none;
        transition: transform 150ms ease, box-shadow 150ms ease;
        overflow: hidden;
        padding: 4px;
        border: none;
    }

    .logo-button img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: transparent;
        display: block;
    }

    .logo-fallback {
        width: 100%;
        height: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #0f172a;
    }

    .logo-fallback svg {
        width: 100%;
        height: 100%;
    }

    .logo-button:active {
        transform: scale(0.96);
    }

    .theme-toggle {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: #475569;
        transition: color 150ms ease, transform 150ms ease;
    }

    .theme-toggle:active {
        transform: scale(0.97);
    }

    .refresh-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.35rem;
        color: #64748b;
        background: transparent;
        border: none;
        border-radius: 0.5rem;
        cursor: pointer;
        transition: all 150ms ease;
    }

    .refresh-button:hover {
        color: #475569;
        background: rgba(148, 163, 184, 0.1);
    }

    .refresh-button:active {
        transform: scale(0.95) rotate(90deg);
    }

    .weather-widget {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.875rem; /* text-sm */
        font-weight: 600; /* font-semibold */
        color: #475569; /* text-slate-600 */
        transition: color 150ms ease;
    }

    .weather-icon {
        color: #64748b;
        flex-shrink: 0;
    }

    .main-clickable {
        cursor: pointer;
    }

    .close-button {
        background: rgba(15, 23, 42, 0.06);
        width: 44px;
        height: 44px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #475569;
        transition: transform 150ms ease, background 150ms ease;
    }

    .close-button:active {
        transform: scale(0.95);
    }

    .field-label {
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.3em;
        color: #94a3b8;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .field-select {
        width: 100%;
        appearance: none;
        border-radius: 1.5rem;
        border: 1px solid rgba(15, 23, 42, 0.12);
        background: rgba(255, 255, 255, 0.9);
        padding: 1.1rem 3rem 1.1rem 1.4rem;
        font-size: 1rem;
        font-weight: 600;
        color: #0f172a;
        transition: border 150ms ease, box-shadow 150ms ease;
    }

    .field-select:focus {
        outline: none;
        border-color: rgba(15, 23, 42, 0.35);
        box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.1);
    }

    .field-select:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .select-wrapper {
        position: relative;
    }

    .select-icon {
        position: absolute;
        right: 1.2rem;
        top: 50%;
        transform: translateY(-50%);
        color: #94a3b8;
        pointer-events: none;
    }

    .single-option {
        padding: 1.1rem 1.4rem;
        border-radius: 1.5rem;
        background: rgba(15, 23, 42, 0.06);
        font-size: 1rem;
        font-weight: 600;
        color: #0f172a;
        border: 1px solid rgba(15, 23, 42, 0.08);
    }

    .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 0.5rem 0;
    }

    .checkbox-label {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        border-radius: 1rem;
        background: rgba(15, 23, 42, 0.02);
        border: 1px solid rgba(15, 23, 42, 0.08);
        cursor: pointer;
        transition: all 150ms ease;
        font-size: 1rem;
        font-weight: 500;
        color: #0f172a;
    }

    .checkbox-label.selected {
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.3);
    }

    .checkbox-label:hover {
        background: rgba(15, 23, 42, 0.06);
        border-color: rgba(15, 23, 42, 0.12);
    }

    .checkbox-label input[type="checkbox"] {
        width: 1.25rem;
        height: 1.25rem;
        cursor: pointer;
        accent-color: #0f172a;
    }

    .checkbox-label input[type="checkbox"]:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .primary-button {
        background: #0f172a;
        color: #fff;
        font-weight: 600;
        padding: 1.1rem 1.6rem;
        border-radius: 1.75rem;
        font-size: 1.05rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        transition: transform 150ms ease, box-shadow 150ms ease;
        box-shadow: 0 18px 35px rgba(15, 23, 42, 0.2);
        min-width: 180px;
        text-align: center;
    }

    .primary-button:active {
        transform: scale(0.98);
    }

    .primary-button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        box-shadow: none;
    }

    .secondary-button {
        padding: 1rem 1.5rem;
        border-radius: 1.75rem;
        background: rgba(15, 23, 42, 0.08);
        color: #0f172a;
        font-size: 0.95rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        transition: transform 150ms ease, background 150ms ease;
        min-width: 140px;
        text-align: center;
    }

    .secondary-button:active {
        transform: scale(0.98);
    }

    .secondary-button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
    }

    .toast {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        padding: 0.9rem 1.4rem;
        border-radius: 9999px;
        background: rgba(15, 23, 42, 0.92);
        color: #fff;
        font-size: 0.9rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        z-index: 60;
        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.25);
    }

    .sidebar-stack {
        gap: 0.75rem;
    }

    .sidebar-cards {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        min-height: 0;
    }

    .sidebar-cards-fill {
        flex: 1;
        display: grid;
        grid-template-rows: repeat(3, minmax(0, 1fr));
        align-content: stretch;
        gap: 0.75rem;
        min-height: 0;
    }

    .sidebar-card {
        padding: 1.35rem;
    }

    .sidebar-card .text-base {
        font-size: 0.95rem;
    }

    .sidebar-fill {
        flex: 1 1 0;
    }

    .alert-card {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.9rem 1.1rem;
        border-radius: 1.2rem;
        background: rgba(15, 23, 42, 0.04);
        border: 1px solid rgba(15, 23, 42, 0.08);
    }

    .route-pill {
        background: #0f172a;
        color: #fff;
    }

    .status-chip {
        padding: 0.4rem 1rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        background: rgba(15, 23, 42, 0.08);
        color: #0f172a;
    }

    .status-chip.live {
        background: rgba(16, 185, 129, 0.15);
        color: #047857;
    }

    .status-chip.scheduled {
        background: rgba(245, 158, 11, 0.18);
        color: #b45309;
    }

    .status-chip.connecting {
        background: rgba(59, 130, 246, 0.15);
        color: #1d4ed8;
    }

    .status-chip.offline {
        background: rgba(248, 113, 113, 0.18);
        color: #b91c1c;
    }

    .load-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.35rem 0.75rem;
        border-radius: 9999px;
        background: rgba(15, 23, 42, 0.08);
        color: #0f172a;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .load-chip.subtle {
        background: rgba(15, 23, 42, 0.06);
        font-size: 0.7rem;
        letter-spacing: 0.12em;
    }

    .arrival-type-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.5rem;
        border-radius: 0.375rem;
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
    }

    .arrival-type-badge.live {
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
        border: 1px solid rgba(16, 185, 129, 0.3);
    }

    .arrival-type-badge.scheduled {
        background: rgba(59, 130, 246, 0.15);
        color: #3b82f6;
        border: 1px solid rgba(59, 130, 246, 0.3);
    }

    .live-indicator {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
    }

    .live-dot {
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        background: currentColor;
        animation: live-pulse 2s ease-in-out infinite;
    }

    @keyframes live-pulse {
        0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 currentColor;
        }
        50% {
            opacity: 0.7;
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.3);
        }
    }

    .freshness-indicator,
    .confidence-indicator {
        display: inline-flex;
        align-items: center;
        padding: 0.35rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
    }

    .freshness-indicator.stale {
        background: rgba(251, 191, 36, 0.15);
        color: #d97706;
        border: 1px solid rgba(251, 191, 36, 0.3);
    }

    .freshness-indicator.very_stale {
        background: rgba(239, 68, 68, 0.15);
        color: #dc2626;
        border: 1px solid rgba(239, 68, 68, 0.3);
    }

    .confidence-indicator.low {
        background: rgba(148, 163, 184, 0.15);
        color: #64748b;
        border: 1px solid rgba(148, 163, 184, 0.3);
    }

    .confidence-indicator.medium {
        background: rgba(59, 130, 246, 0.15);
        color: #2563eb;
        border: 1px solid rgba(59, 130, 246, 0.3);
    }

    @keyframes panelRise {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .panel-rise {
        animation: panelRise 500ms ease both;
        will-change: transform, opacity;
    }

    .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(15, 23, 42, 0.15);
        border-radius: 10px;
    }

    select option {
        background: #ffffff;
        color: #0f172a;
        font-weight: 600;
    }

    /* Loading skeleton animations */
    .field-select.loading {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        color: transparent;
        pointer-events: none;
    }

    @keyframes shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }

    :global(body.theme-dark) .field-select.loading {
        background: linear-gradient(90deg, #1e293b 25%, #334155 50%, #1e293b 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
    }

    :global(body.theme-dark) {
        background: #0b0d12;
        color: #e2e8f0;
    }

    :global(body.theme-dark) .kiosk {
        background: radial-gradient(circle at 20% 10%, rgba(30, 41, 59, 0.55), transparent 55%),
            radial-gradient(circle at 80% 30%, rgba(15, 23, 42, 0.7), transparent 45%),
            linear-gradient(135deg, #0b0d12 0%, #111827 50%, #0b0d12 100%);
    }

    :global(body.theme-dark) .glass-card {
        background: rgba(15, 23, 42, 0.96);
        border: 1px solid rgba(148, 163, 184, 0.15);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    }

    :global(body.theme-dark) .logo-button {
        background: transparent;
        color: #e2e8f0;
        box-shadow: none;
    }

    :global(body.theme-dark) .logo-fallback {
        color: #e2e8f0;
    }

    :global(body.theme-dark) .pill-button,
    :global(body.theme-dark) .primary-button {
        background: #e2e8f0;
        color: #0f172a;
    }

    :global(body.theme-dark) .secondary-button {
        background: rgba(148, 163, 184, 0.12);
        color: #e2e8f0;
    }

    :global(body.theme-dark) .alert-card {
        background: rgba(148, 163, 184, 0.08);
        border-color: rgba(148, 163, 184, 0.2);
    }

    :global(body.theme-dark) .theme-toggle,
    :global(body.theme-dark) .weather-widget,
    :global(body.theme-dark) .refresh-button,
    :global(body.theme-dark) .field-label,
    :global(body.theme-dark) .select-icon {
        color: #94a3b8;
    }

    :global(body.theme-dark) .refresh-button:hover {
        color: #cbd5e1;
        background: rgba(148, 163, 184, 0.15);
    }

    :global(body.theme-dark) .field-select {
        background: rgba(15, 23, 42, 0.9);
        border-color: rgba(148, 163, 184, 0.3);
        color: #e2e8f0;
    }

    :global(body.theme-dark) .field-select:focus {
        border-color: rgba(226, 232, 240, 0.4);
        box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.2);
    }

    :global(body.theme-dark) .single-option {
        background: rgba(148, 163, 184, 0.12);
        border-color: rgba(148, 163, 184, 0.2);
        color: #e2e8f0;
    }

    :global(body.theme-dark) .checkbox-label {
        background: rgba(148, 163, 184, 0.08);
        border-color: rgba(148, 163, 184, 0.15);
        color: #e2e8f0;
    }

    :global(body.theme-dark) .checkbox-label.selected {
        background: rgba(59, 130, 246, 0.2);
        border-color: rgba(59, 130, 246, 0.4);
    }

    :global(body.theme-dark) .checkbox-label:hover {
        background: rgba(148, 163, 184, 0.15);
        border-color: rgba(148, 163, 184, 0.25);
    }

    :global(body.theme-dark) .checkbox-label input[type="checkbox"] {
        accent-color: #e2e8f0;
    }

    :global(body.theme-dark) .close-button {
        background: rgba(148, 163, 184, 0.15);
        color: #e2e8f0;
    }

    :global(body.theme-dark) .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.35);
    }

    :global(body.theme-dark) select option {
        background: #0f172a;
        color: #e2e8f0;
    }

    :global(body.theme-dark) .text-slate-900 {
        color: #f8fafc;
    }

    :global(body.theme-dark) .text-slate-700 {
        color: #cbd5e1;
    }

    :global(body.theme-dark) .text-slate-600,
    :global(body.theme-dark) .text-slate-500 {
        color: #94a3b8;
    }

    :global(body.theme-dark) .text-slate-400,
    :global(body.theme-dark) .text-slate-300 {
        color: #64748b;
    }

    :global(body.theme-dark) .bg-slate-900 {
        background: #e2e8f0;
        color: #0f172a;
    }

    :global(body.theme-dark) .bg-slate-200 {
        background: rgba(148, 163, 184, 0.25);
    }

    /* P1-6: Error State UI Styles */
    .error-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1.5rem;
        padding: 3rem 2rem;
        text-align: center;
        margin-bottom: 2rem;
    }

    .error-icon {
        color: #dc2626;
        opacity: 0.8;
        animation: shake 0.5s ease-in-out;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
    }

    .error-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0;
    }

    .error-message {
        color: var(--text-secondary);
        max-width: 400px;
        font-size: 1rem;
        line-height: 1.6;
    }

    .error-actions {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        justify-content: center;
    }

    .retry-button,
    .settings-error-button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        border-radius: 0.5rem;
        font-weight: 600;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
    }

    .retry-button {
        background: #3b82f6;
        color: white;
    }

    .retry-button:hover {
        background: #2563eb;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
    }

    .settings-error-button {
        background: transparent;
        color: #475569;
        border: 2px solid #e2e8f0;
    }

    .settings-error-button:hover {
        border-color: #3b82f6;
        color: #3b82f6;
    }

    :global(body.theme-dark) .error-title {
        color: #f8fafc;
    }

    :global(body.theme-dark) .error-message {
        color: #94a3b8;
    }

    :global(body.theme-dark) .settings-error-button {
        color: #cbd5e1;
        border-color: #334155;
    }

    :global(body.theme-dark) .settings-error-button:hover {
        border-color: #3b82f6;
        color: #60a5fa;
    }

    /* P1-5: Focus indicators for accessibility */
    *:focus-visible {
        outline: 3px solid #3b82f6;
        outline-offset: 2px;
    }

    :global(body.theme-dark) *:focus-visible {
        outline-color: #60a5fa;
    }

    /* Screen reader only text */
    /* Field hint for form feedback */
    .field-hint {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: var(--text-secondary);
        margin-top: 0.5rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        background: rgba(148, 163, 184, 0.1);
    }

    .field-hint.warning {
        color: #d97706;
        background: rgba(217, 119, 6, 0.1);
    }

    :global(body.theme-dark) .field-hint.warning {
        color: #fbbf24;
        background: rgba(251, 191, 36, 0.1);
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
    }
</style>
